import type { FormEvent, ReactElement, ReactNode } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router';
import type {
  ChatMessage,
  Commitment,
  Decision,
  MeetingDetail,
  PresenceEvent,
  TranscriptLine,
} from '@contracts/meetings';
import type { CaptureHealth, LinkAccess } from '@contracts/common';
import { ApiError } from '../api/client';
import {
  useBotAction,
  useCorrectLine,
  useDeleteMeeting,
  useEventBrief,
  useKnowledge,
  useKnowledgeActions,
  useLedgerActions,
  useMe,
  useMeeting,
  useMeetingChat,
  useMeetingPresence,
  useNames,
  useOrg,
  useRecordOverride,
  useRewrite,
  useSendBot,
  useSeriesList,
  useShareActions,
  useSharing,
  useChannels,
  useChannelActions,
  useTranscript,
  useUpdateCommitment,
  useRewriteNote,
  useSaveNote,
  useUpdateMeeting,
  useWalkthrough,
} from '../api/queries';
import {
  BackLink,
  Broken,
  Button,
  Card,
  Confirm,
  Empty,
  Field,
  Grade,
  Icon,
  MenuButton,
  Modal,
  OwnerModal,
  Pill,
  Room,
  Rows,
  SearchField,
  Picker,
  SkeletonRows,
  Switch,
  TextArea,
  Who,
  useNotices,
} from '../ui';
import { MeetingPlayer, sections, secondsOfLine, timecode } from '../meeting/Player';
import { Screenshots } from '../meeting/Screenshots';
import { BriefBody } from './BriefBody';
import { DueButton } from '../ledger/DueButton';
import { AsanaModal } from '../ui/AsanaModal';

/**
 * What happened to a commitment that is no longer open, in one word.
 *
 * ⚠ Never "closed" for all of them. Vladimir, 2026-09-23: *"I marked one
 * commitment that no one will do it and app closed it instead of drop it."* It
 * WAS dropped — the row just said "closed", which reads as "dealt with" and is
 * the opposite of what he had said.
 */
function ended(status: Commitment['status']): string {
  switch (status) {
    case 'DROPPED':
      return 'dropped';
    case 'DONE':
      return 'done';
    case 'HANDED_OVER':
      return 'handed over';
    case 'EXPORTED':
      return 'sent to Asana';
    case 'SUPERSEDED':
      return 'replaced';
    default:
      return 'closed';
  }
}

/** One array each, so "nothing yet" is not a new array on every render. */
const EMPTY_CHAT: ChatMessage[] = [];
const EMPTY_PRESENCE: PresenceEvent[] = [];
import {
  Questions,
  RecordControls,
  RecordEditor,
  RecordSection,
  Risks,
  SeriesSuggestion,
} from '../meeting/Records';
import { SlackPost } from './SlackPost';
import { AddAction } from '../meeting/NewAction';
import { Translated } from '../meeting/Translated';
import { PostToChannels } from '../meeting/PostToChannels';
import { SendNoteDm } from '../meeting/SendNoteDm';
import { NoteVersions } from '../meeting/Writeups';
import { EditableNote } from '../editor/EditableNote';
import { useTab, useUrlText } from '../lib/url-tab';
import { NoteText } from '../editor/NoteText';
import { between, clock, clockAt, dateLabel, duration, since } from '../lib/time';
import { copyText, firstName, guessName, sentenceCount, summaryOf, titleOf } from '../lib/note';
import { byLastMet, seriesWhen } from '../series/when';
import { whyNothing } from '../meeting/why-nothing';

type View = 'brief' | 'summary' | 'record' | 'note' | 'walkthrough' | 'typed' | 'transcript';

/** A meeting that has not finished: the notetaker is on its way, or in the call. */
const HAPPENING: CaptureHealth[] = ['SCHEDULED', 'JOINING', 'WAITING_ROOM', 'RECORDING', 'PAUSED'];

/** On its way, but nothing has been said yet. */
const NOT_STARTED: CaptureHealth[] = ['SCHEDULED', 'JOINING', 'WAITING_ROOM'];
const VIEWS: Array<[View, string]> = [
  ['brief', 'Before'],
  ['summary', 'Summary'],
  // ⚠ Its own tab, not sections under the summary. Vladimir, 2026-09-22:
  // "let's move the decisions, questions, risk to the separate tab, not the
  // summary" — overruling my judgement that the page already had enough tabs.
  // He is the one reading these every day: what was settled, what might go
  // wrong and what nobody answered are a thing you go TO, not something you
  // scroll past on the way out of a summary.
  // "Decisions", not "Record": Aleksandr opened "Record" looking for the video
  // recording (Vladimir, 2026-10-08). The video is on the Walkthrough tab.
  ['record', 'Decisions'],
  ['note', 'Full note'],
  ['walkthrough', 'Walkthrough'],
  ['typed', 'What you typed'],
  ['transcript', 'Transcript'],
];

/** The tabs showing what the product wrote — the ones a translation applies to (PRD 57). */
const TRANSLATED_VIEWS: View[] = ['summary', 'note', 'record', 'walkthrough'];

/** Which tab is waiting on which job. Only the three that are written by one. */
const MAKING: Partial<Record<View, (p: MeetingDetail['processing']) => boolean>> = {
  summary: (p) => p.note || p.ledger,
  note: (p) => p.note,
  walkthrough: (p) => p.walkthrough,
};

/**
 * Boards 05 + 22 — one meeting, four views of the same hour behind one switch
 * in a fixed order, and what it produced. Everything the reader may do is in
 * `access`, so a control is shown only when it will work.
 */
export function Meeting(): ReactElement {
  const { id = '' } = useParams();
  // "Show original" (PRD 57) — for this meeting only; the next one opens in
  // the reader's language again.
  const [originalOf, setOriginalOf] = useState<string | null>(null);
  const original = originalOf === id;
  const meeting = useMeeting(id, { original });
  if (meeting.isPending) {
    return (
      <div className="page">
        <SkeletonRows rows={4} />
      </div>
    );
  }
  if (meeting.isError) {
    const gone = meeting.error instanceof ApiError && meeting.error.status === 404;
    return (
      <div className="page">
        {gone ? (
          <Card flush>
            <Empty
              tone="bad"
              lead="You are not in this meeting."
              action={
                <Link to="/meetings" className="btn">
                  Your meetings
                </Link>
              }
            >
              A meeting belongs to the people who were there. If somebody meant you to see it, ask
              them to share it with you.
            </Empty>
          </Card>
        ) : (
          <Card flush>
            <Broken what="This meeting" retry={() => void meeting.refetch()} />
          </Card>
        )}
      </div>
    );
  }
  return (
    <Loaded
      meeting={meeting.data}
      original={original}
      onToggleOriginal={() => setOriginalOf(original ? null : id)}
    />
  );
}

function Loaded({
  meeting,
  original,
  onToggleOriginal,
}: {
  meeting: MeetingDetail;
  original: boolean;
  onToggleOriginal: () => void;
}): ReactElement {
  // `#line-<id>` — arriving from a commitment on Open — lands on the transcript at that line.
  const { hash } = useLocation();
  const line = hash.startsWith('#line-') ? hash.slice('#line-'.length) : null;
  // While it is happening, you land on the words as they are said. Vladimir,
  // 2026-10-08: "i want the page to open on trascript during the call, not on
  // my notes, my notes is not used almost" — Your notes is one tab away.
  const live = HAPPENING.includes(meeting.captureHealth);
  // The brief is a block on this page, always available while the meeting it
  // was written for still exists. ⚠ NOT only before the start: "maybe I will
  // need it after meeting started so I can read it in the beginning of the
  // meeting while we wait for everyone to join" (Vladimir, 2026-09-21). What
  // the start changes is only which tab you land on.
  const brief = useEventBrief(meeting.event?.id ?? '');
  const before = NOT_STARTED.includes(meeting.captureHealth);
  // Where the page lands when its address names no tab — decided once, on
  // arrival: a meeting ending under somebody typing their notes must not
  // switch them to the summary.
  // ⚠ A series with its own template opens on that note (PRD 65) — Vladimir:
  // "it shall open on that note, not the main" — once it has been written.
  const [arrival] = useState<View>(() =>
    line
      ? 'transcript'
      : before
        ? 'brief'
        : live
          ? 'transcript'
          : meeting.seriesNote?.writeupId
            ? 'note'
            : 'summary',
  );
  const tabs = VIEWS.filter(([key]) => key !== 'brief' || !!meeting.event).map(([key]) => key);
  // In the address (`?tab=`), so a reload and Back keep it (2026-09-25).
  const [view, setView] = useTab<View>('tab', tabs, arrival, { keepFallback: true });
  const [jumpTo, setJumpTo] = useState<string | null>(line);
  // The video and the transcript, on one clock (PRD 55). `nonce` is what makes
  // asking for the same second twice a new request rather than a no-op.
  const [seekTo, setSeekTo] = useState<{ seconds: number; nonce: number } | null>(null);
  const seek = useCallback((seconds: number) => {
    setSeekTo({ seconds, nonce: performance.now() });
  }, []);
  // ⚠ A seek is spent once the tab that asked for it is left. The player mounts
  // afresh every time the Walkthrough tab opens, and a seek still standing
  // would jump — and play — the second you came back.
  useEffect(() => {
    if (view !== 'walkthrough') setSeekTo(null);
  }, [view]);
  const [sharing, setSharing] = useState(window.location.hash === '#share');
  const [dming, setDming] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const remove = useDeleteMeeting(meeting.id);
  const navigate = useNavigate();
  const me = useMe();
  const transcript = useTranscript(meeting.id, meeting.access.canReadTranscript, live);
  // What people TYPED (PRD 50) — the transcript's access rule, so it is asked
  // for on exactly the same condition.
  const chat = useMeetingChat(meeting.id, meeting.access.canReadTranscript);
  // Who came and went (2026-09-25): the same rule, and it follows the room live.
  const presence = useMeetingPresence(meeting.id, meeting.access.canReadTranscript, live);
  const people = useNames();
  const names = useMemo(
    () => new Map([...people, ...namesFrom(transcript.data ?? [])]),
    [people, transcript.data],
  );
  const voices =
    new Set((transcript.data ?? []).map((l) => l.speakerEmail ?? l.speakerName).filter(Boolean))
      .size ||
    meeting.attendeeEmails.length ||
    meeting.attendees.length;
  const seriesList = useSeriesList();
  const seriesTitle = seriesList.data?.find((x) => x.seriesKey === meeting.seriesKey)?.title;
  const { show } = useNotices();

  // Fetched when the tab is opened, not before: it is the longest thing the
  // meeting has and most readers never ask for it. The chapters ARE the
  // video's sections (PRD 55), and both live on the Walkthrough tab.
  const walkthrough = useWalkthrough(meeting.id, view === 'walkthrough', {
    original,
    waiting: !!meeting.translation?.pending,
  });

  const videoZero = meeting.video.startedAt ? new Date(meeting.video.startedAt).getTime() : null;
  // Each chapter's second in the video, by the same rule the strip uses — so a
  // chapter row and its button under the player can never show two clocks.
  const placed = useMemo(
    () =>
      new Map(
        meeting.video.available
          ? sections(walkthrough.data ?? [], transcript.data ?? [], videoZero).map((s) => [
              s.id,
              s.seconds,
            ])
          : [],
      ),
    [meeting.video.available, walkthrough.data, transcript.data, videoZero],
  );
  // A citation, a walkthrough section, a commitment's evidence: every one of
  // them points at a line, and opens the transcript there. The line's own time
  // is the way on to the recording.
  const jump = (segmentId: string | null) => {
    if (!segmentId) return;
    setView('transcript');
    setJumpTo(segmentId);
  };
  // From a transcript line to the recording: the Walkthrough tab, at that second.
  const watch = (seconds: number) => {
    setView('walkthrough');
    seek(seconds);
  };
  // On the Walkthrough tab: move the recording, and bring it into view — the
  // chapter somebody pressed may be far below it.
  const play = (seconds: number) => {
    seek(seconds);
    const still = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    document
      .querySelector('.player')
      ?.scrollIntoView({ behavior: still ? 'auto' : 'smooth', block: 'start' });
  };
  // A row of "What it produced": switch to its tab, then bring its section
  // into view once the tab has rendered — the section does not exist before.
  const [landing, setLanding] = useState<{ anchor: string; at: number } | null>(null);
  const go = useCallback(
    (to: Landing) => {
      setView(to.view);
      setLanding({ anchor: to.anchor, at: performance.now() });
    },
    [setView],
  );
  useEffect(() => {
    if (!landing) return;
    const frame = requestAnimationFrame(() => {
      const el = document.getElementById(landing.anchor);
      if (!el) return;
      const still = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      el.scrollIntoView({ behavior: still ? 'auto' : 'smooth', block: 'start' });
      // Said where it landed: on a short page the section may already have
      // been on screen, and a press that moves nothing reads as a dead button.
      if (!still) {
        const ring = getComputedStyle(document.documentElement).getPropertyValue('--green');
        el.animate([{ boxShadow: `0 0 0 3px ${ring}` }, { boxShadow: '0 0 0 3px transparent' }], {
          duration: 1600,
          easing: 'ease-out',
        });
      }
    });
    return () => cancelAnimationFrame(frame);
  }, [landing]);
  const copy = async (what: 'summary' | 'note' | 'transcript') => {
    const text =
      what === 'summary'
        ? summaryOf(meeting.polishedNotes).join('\n\n')
        : what === 'note'
          ? (meeting.polishedNotes ?? '')
          : (transcript.data ?? [])
              .map(
                (l) =>
                  `${l.spokeAt ? clock(l.spokeAt) : '--:--'}  ${l.speakerName ?? l.speakerEmail ?? '?'}: ${l.text}`,
              )
              .join('\n');
    if (!text) return show({ tone: 'attention', text: 'Nothing to copy yet.' });
    await copyText(text);
    show({
      tone: 'success',
      text:
        what === 'summary'
          ? 'Summary copied.'
          : what === 'note'
            ? 'The full note is on your clipboard as Markdown.'
            : 'Transcript copied, with timestamps.',
    });
  };

  return (
    <div className="page stack-lg">
      <div className="toolbar">
        <BackLink to="/meetings">Meetings</BackLink>
        <span className="crumb">
          {/* ⚠ Its series' page whether or not anything of it is recorded yet:
              its settings are for the meetings to come (PRD 65). */}
          {meeting.seriesKey ? (
            <Link to={`/series/${encodeURIComponent(meeting.seriesKey)}`}>
              {seriesTitle ?? meeting.title}
            </Link>
          ) : (
            'Meetings'
          )}{' '}
          · {dayMonth(meeting.startedAt)}
        </span>
        <div className="actions">
          <Link to={`/m/${meeting.id}/answer`} className="btn">
            {minutesOf(meeting) > 0
              ? `Ask these ${minutesOf(meeting)} minutes`
              : 'Ask this meeting'}
          </Link>
          <MenuButton
            label="Copy or export"
            items={[
              { label: 'Copy the summary', onSelect: () => void copy('summary') },
              { label: 'Copy the full note as Markdown', onSelect: () => void copy('note') },
              {
                label: 'Send the full note to a person on Slack…',
                sub: 'from you, in your own DM with them',
                onSelect: () => setDming(true),
                disabledBecause: meeting.polishedNotes?.trim() ? undefined : 'no note yet',
              },
              {
                label: 'Copy the transcript with timestamps',
                onSelect: () => void copy('transcript'),
                disabledBecause: meeting.access.canReadTranscript ? undefined : 'not yours to copy',
              },
            ]}
          />
          {meeting.access.canManage ? (
            <>
              <Button onClick={() => setSharing(true)}>Share…</Button>
              <MenuButton
                label="···"
                variant="quiet"
                items={[
                  {
                    label: 'Delete this meeting…',
                    danger: true,
                    onSelect: () => setDeleting(true),
                  },
                ]}
              />
            </>
          ) : null}
        </div>
      </div>
      <div className="between" style={{ alignItems: 'flex-start' }}>
        <div className="stack" style={{ gap: 4 }}>
          <Title meeting={meeting} />
          <p className="mono">
            {dateLabel(meeting.startedAt)} · {clock(meeting.startedAt)}
            {meeting.endedAt ? `–${clock(meeting.endedAt)}` : ''} ·{' '}
            {/* A meeting still going has no length yet, and "under a minute"
                is a lie about a call that has been running twenty. */}
            {/* A meeting that has not started has no length and is not going;
                one being recorded has no length YET. */}
            {before
              ? `starts ${since(meeting.startedAt)}`
              : live
                ? 'still going'
                : duration(between(meeting.startedAt, meeting.endedAt))}{' '}
            · {voices} {voices === 1 ? 'voice' : 'voices'}
            {meeting.recordedById && meeting.recordedById === me.data?.id
              ? ' · recorded by you'
              : ''}
            {' · '}
            <SeriesMark meeting={meeting} />
          </p>
          <InChannels meeting={meeting} />
        </div>
        <Attendees meeting={meeting} names={names} myEmail={me.data?.email ?? ''} />
      </div>

      <LiveBar meeting={meeting} />

      <div className="subtabs" role="tablist">
        {VIEWS.filter(([key]) => tabs.includes(key)).map(([key, label0]) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={view === key}
            className={`tab ${view === key ? 'on' : ''}`}
            onClick={() => setView(key)}
            style={{
              background: 'none',
              border: 0,
              borderBottom: view === key ? '2px solid var(--green)' : '2px solid transparent',
              cursor: 'pointer',
            }}
          >
            {key === 'typed' && live ? 'Your notes' : label0}
            {key === 'typed' && meeting.notes.trim() ? (
              <span className="mono" style={{ marginLeft: 6 }}>
                {meeting.notes.split('\n').filter((l) => l.trim()).length}
              </span>
            ) : null}
            {key === 'transcript' && transcript.data ? (
              <span className="mono" style={{ marginLeft: 6 }}>
                {transcript.data.length} turns
              </span>
            ) : null}
            {/* ⚠ On the TAB, because that is where somebody decides whether to
                open it — "add processing state to the meeting and tabs, as now
                I don't understand if it's just processing or something is
                already broke" (Vladimir, 2026-09-21). An amber dot is a wait;
                a red one is a wait that has gone on too long to be one. */}
            {MAKING[key]?.(meeting.processing) ? (
              <span
                aria-label={meeting.processing.stalled ? 'late' : 'being written'}
                title={
                  meeting.processing.stalled
                    ? 'Should have been written by now'
                    : 'Being written now'
                }
                style={{
                  marginLeft: 6,
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  display: 'inline-block',
                  verticalAlign: 'middle',
                  background: meeting.processing.stalled ? 'var(--red)' : 'var(--amber)',
                }}
              />
            ) : null}
          </button>
        ))}
      </div>

      {/* What the product wrote, in the reader's language (PRD 57) — said on
          every tab that shows it, and never on the transcript, which is what
          was said and is never translated. */}
      {TRANSLATED_VIEWS.includes(view) ? (
        <Translated state={meeting.translation} onToggle={onToggleOriginal} />
      ) : null}
      <SeriesSuggestion meeting={meeting} />
      {view === 'brief' ? (
        brief.isPending ? (
          <SkeletonRows rows={6} />
        ) : brief.isError || !brief.data ? (
          <Card flush>
            <Empty lead="No brief for this one.">
              A brief is composed from the meetings and people you can already open. There is
              nothing to go on yet for this meeting.
            </Empty>
          </Card>
        ) : (
          <BriefBody b={brief.data} />
        )
      ) : null}
      {view === 'walkthrough' ? (
        <div className="stack-lg">
          <MeetingPlayer
            meeting={meeting}
            chapters={walkthrough.data ?? []}
            lines={transcript.data ?? []}
            seekTo={seekTo}
          />
          <Screenshots meeting={meeting} onPlay={meeting.video.available ? play : null} />
          <Walkthrough
            chapters={walkthrough}
            pending={meeting.processing.walkthrough}
            stalled={meeting.processing.stalled}
            onJump={jump}
            placed={placed}
            onPlay={meeting.video.available ? play : null}
            rewrite={<Rewrite meeting={meeting} />}
          />
        </div>
      ) : null}
      {view === 'summary' ? (
        <>
          <Summary meeting={meeting} onJump={jump} onGo={go} names={names} />
          {/* "Ptitim, screenshot" (PRD 64): kept beside the video, and said here
              too — the summary is where people land after the call. */}
          {meeting.screenshots?.length ? (
            <button type="button" className="linkish" onClick={() => setView('walkthrough')}>
              📸 {meeting.screenshots.length}{' '}
              {meeting.screenshots.length === 1 ? 'screenshot' : 'screenshots'} from the call — on
              the Walkthrough tab
            </button>
          ) : null}
        </>
      ) : null}
      {view === 'record' ? <Record meeting={meeting} onJump={jump} /> : null}
      {view === 'note' ? (
        // The full note, and beside it every write-up of it (PRD 61) — the
        // note is always the first version and never replaced.
        <NoteVersions
          meeting={meeting}
          lines={transcript.data?.length ?? null}
          full={
            <FullNote
              key={meeting.id}
              meeting={meeting}
              lines={transcript.data?.length ?? null}
              onShowOriginal={onToggleOriginal}
            />
          }
        />
      ) : null}
      {view === 'typed' ? (
        <Typed meeting={meeting} lines={transcript.data?.length ?? null} />
      ) : null}
      {view === 'transcript' ? (
        <Transcript
          meeting={meeting}
          lines={transcript}
          chat={chat.data ?? EMPTY_CHAT}
          presence={presence.data ?? EMPTY_PRESENCE}
          jumpTo={jumpTo}
          names={names}
          videoZero={videoZero}
          onWatch={meeting.video.available ? watch : null}
        />
      ) : null}

      {dming ? (
        <SendNoteDm
          meetingId={meeting.id}
          attendees={meeting.attendees}
          onClose={() => setDming(false)}
        />
      ) : null}
      {sharing ? (
        <ShareModal meeting={meeting} names={names} onClose={() => setSharing(false)} />
      ) : null}
      {deleting ? (
        <Confirm
          title="Delete this meeting"
          noun="Delete the meeting"
          working={remove.isPending}
          onClose={() => setDeleting(false)}
          onConfirm={() =>
            remove.mutate(undefined, {
              onSuccess: () => {
                show({ tone: 'success', text: `“${meeting.title}” is deleted, for everyone.` });
                void navigate('/meetings');
              },
              onError: (err) => {
                // A 409 is the notetaker still in the call; the server's sentence says so.
                show({ tone: 'failure', text: err.message });
                setDeleting(false);
              },
            })
          }
        >
          The note, the transcript, the video, its commitments and decisions, and anything learned
          only from this meeting — deleted for you and for everyone it was shared with. It cannot be
          undone; backups expire within 30 days.
        </Confirm>
      ) : null}
    </div>
  );
}

/**
 * The series a meeting belongs to — and, for an editor, a way to attach a
 * one-off to one, or detach it. A series is the room a commitment rolls
 * forward in; a series itself is a recurring calendar event and is not
 * created here.
 */
function SeriesMark({ meeting }: { meeting: MeetingDetail }): ReactElement {
  const list = useSeriesList();
  const update = useUpdateMeeting(meeting.id);
  const { show } = useNotices();
  const navigate = useNavigate();
  const known = list.data ?? [];
  // ⚠ Never its key: a series this page cannot name yet is called after the
  // meeting it is on (it read "in own:557c5855-…" the morning it shipped).
  const current = known.find((x) => x.seriesKey === meeting.seriesKey)?.title ?? meeting.title;
  if (!meeting.access.canEdit) {
    return meeting.seriesKey ? (
      <Link to={`/series/${encodeURIComponent(meeting.seriesKey)}`}>in {current}</Link>
    ) : (
      <span>no series</span>
    );
  }
  const attach = (seriesKey: string | null, title: string | null, seriesTitle?: string) =>
    update.mutate(
      { seriesKey, ...(seriesTitle ? { seriesTitle } : {}) },
      {
        onSuccess: () =>
          show({
            tone: 'success',
            text: seriesKey
              ? `Attached to ${title} — what it produced rolls forward with the room.`
              : 'Detached — it stands on its own now.',
          }),
        onError: (err) => show({ tone: 'failure', text: err.message }),
      },
    );
  return (
    <MenuButton
      size="sm"
      variant="quiet"
      align="left"
      label={meeting.seriesKey ? `in ${current}` : 'no series'}
      // ⚠ Newest first. A menu of series is ordered by when you last met, not
      // alphabetically: the one you want is nearly always one you met
      // recently, and past ten of them the filter is what finds the rest.
      items={[
        // ⚠ The way INTO the series, first. This menu only changed which series
        // a meeting is in, so an editor had no way from here to the series'
        // own page (Vladimir and Aleksandr, 2026-10-08: "нельзя провалиться
        // напрямую из встречи в серию").
        ...(meeting.seriesKey
          ? [
              {
                label: `Open “${current}”`,
                sub: 'its settings, its meetings and what rolls forward',
                onSelect: () => void navigate(`/series/${encodeURIComponent(meeting.seriesKey!)}`),
              },
            ]
          : []),
        ...[...known].sort(byLastMet).map((x) => ({
          label: x.title,
          sub: seriesWhen(x, 'instance'),
          current: x.seriesKey === meeting.seriesKey,
          onSelect:
            x.seriesKey === meeting.seriesKey ? undefined : () => attach(x.seriesKey, x.title),
        })),
        ...(meeting.seriesKey && !known.some((x) => x.seriesKey === meeting.seriesKey)
          ? [{ label: current, current: true }]
          : []),
      ]}
      // Never filtered away: whatever you typed, making a new one and taking
      // this meeting out of its series are always the last two rows.
      fixed={[
        {
          // ⚠ Without this the menu is a dead end for somebody with no series
          // yet — which is everybody at the start. Vladimir, 2026-09-21: "I
          // had yesterday 4 test events, I want them all to be in one series,
          // how to do this?" You could join one, and never make one.
          label: '+ New series…',
          sub: 'name it, and this meeting is its first',
          onSelect: () => {
            const name = window.prompt('What is this series called?')?.trim();
            if (!name) return;
            // Opaque and ours, so it can never collide with a calendar's own
            // recurring-event id.
            attach(`own:${crypto.randomUUID()}`, name, name);
          },
        },
        {
          label: 'No series',
          sub: 'a one-off — nothing rolls forward from it',
          current: !meeting.seriesKey,
          onSelect: meeting.seriesKey ? () => attach(null, null) : undefined,
        },
      ]}
    />
  );
}

const dayMonth = (iso: string): string =>
  new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'long' }).format(new Date(iso));

/** Two plates and a count — the room, not a roster. */
function Attendees({
  meeting,
  names,
  myEmail,
}: {
  meeting: MeetingDetail;
  names: Map<string, string>;
  myEmail: string;
}): ReactElement {
  const all = meeting.attendeeEmails.length ? meeting.attendeeEmails : meeting.attendees;
  const shown = all.slice(0, 2);
  const rest = all.length - shown.length;
  return (
    <div className="cluster" style={{ justifyContent: 'flex-end', flex: 'none', paddingTop: 6 }}>
      {shown.map((a) => (
        <Who
          key={a}
          email={a.includes('@') ? a : ''}
          name={names.get(a.toLowerCase()) ?? (a.includes('@') ? guessName(a) : a)}
          short
          me={myEmail === a.toLowerCase()}
          to={a.includes('@') ? `/people/${encodeURIComponent(a)}` : undefined}
        />
      ))}
      {rest > 0 ? <span className="t-secondary">and {rest} more</span> : null}
    </div>
  );
}

function namesFrom(lines: TranscriptLine[]): Map<string, string> {
  const names = new Map<string, string>();
  for (const line of lines)
    if (line.speakerEmail && line.speakerName && !names.has(line.speakerEmail.toLowerCase()))
      names.set(line.speakerEmail.toLowerCase(), line.speakerName);
  return names;
}

/** The title, and — for an editor — the one field that renames it. */
function Title({ meeting }: { meeting: MeetingDetail }): ReactElement {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(meeting.title);
  const update = useUpdateMeeting(meeting.id);
  const { show } = useNotices();
  useEffect(() => setValue(meeting.title), [meeting.title]);
  const save = (e: FormEvent) => {
    e.preventDefault();
    const title = value.trim();
    if (!title || title === meeting.title) return setEditing(false);
    update.mutate(
      { title },
      {
        onSuccess: () => {
          setEditing(false);
          show({ tone: 'success', text: 'Renamed.' });
        },
        onError: (err) => show({ tone: 'failure', text: err.message }),
      },
    );
  };
  if (editing) {
    return (
      <form onSubmit={save} className="cluster grow" style={{ flexWrap: 'nowrap' }}>
        <Field
          value={value}
          onChange={(e) => setValue(e.target.value)}
          autoFocus
          aria-label="Title"
          onKeyDown={(e) => e.key === 'Escape' && setEditing(false)}
        />
        <Button type="submit" variant="primary" working={update.isPending ? 'Saving…' : false}>
          Save
        </Button>
        <Button variant="quiet" onClick={() => setEditing(false)}>
          Cancel
        </Button>
      </form>
    );
  }
  return (
    <h1 className="t-title cluster" style={{ gap: 10 }}>
      {meeting.title}
      {meeting.access.canEdit ? (
        <Button size="sm" variant="quiet" onClick={() => setEditing(true)} aria-label="Rename">
          Rename
        </Button>
      ) : null}
    </h1>
  );
}

function Summary({
  meeting,
  onJump,
  onGo,
  names,
}: {
  meeting: MeetingDetail;
  onJump: (segmentId: string | null) => void;
  onGo: (to: Landing) => void;
  names: Map<string, string>;
}): ReactElement {
  const paragraphs = summaryOf(meeting.polishedNotes);
  return (
    <div className="stack-lg">
      <div className="grid-2 aside-fluid">
        <Card
          title={titleOf(meeting.polishedNotes) ?? 'Summary'}
          aside={paragraphs.length ? <Pill>{sentenceCount(paragraphs)} sentences</Pill> : null}
        >
          {paragraphs.length ? (
            <div className="stack" style={{ gap: 12 }}>
              {paragraphs.map((p, i) => (
                <p key={i} className="t-body" style={{ maxWidth: '65ch' }}>
                  {p}
                </p>
              ))}
              <p
                className="t-secondary"
                style={{ borderTop: '1px solid var(--line)', paddingTop: 12, marginTop: 4 }}
              >
                The summary is what you read when you were not there. Two paragraphs, no bullets, no
                headings — the full note is one tab away.
              </p>
            </div>
          ) : (
            <NoteState meeting={meeting} />
          )}
        </Card>
        <div className="stack-lg">
          <ProducedCounts meeting={meeting} onGo={onGo} names={names} />
          <HowItWent meeting={meeting} />
        </div>
      </div>
      <ChangesWhatWeKnow meetingId={meeting.id} />
      {/* ⚠ Four records, not one card and not four more tabs. The page already
          carries six tabs and four more would be ten, which is a row nobody
          reads — so the split is into sections, each with its own kind, count
          and actions (PRD 54). What "split" has to mean is that a risk is a
          risk rather than a sentence in a paragraph. */}
      <Produced meeting={meeting} onJump={onJump} names={names} />
    </div>
  );
}

/**
 * What came out of the meeting that is not a task (PRD 54).
 *
 * ⚠ Three sections and its own tab, because each is a different question:
 * what was settled, what might go wrong, what nobody answered. They were
 * under the summary for an afternoon and Vladimir moved them out — a record
 * you consult is not something you meet on the way past.
 */
function Record({
  meeting,
  onJump,
}: {
  meeting: MeetingDetail;
  onJump: (segmentId: string | null) => void;
}): ReactElement {
  return (
    <div className="stack-lg">
      <Decisions meeting={meeting} onJump={onJump} />
      <Risks meeting={meeting} onJump={onJump} />
      <Questions meeting={meeting} onJump={onJump} />
    </div>
  );
}

function minutesOf(meeting: MeetingDetail): number {
  return meeting.endedAt ? Math.round(between(meeting.startedAt, meeting.endedAt) / 60) : 0;
}

/**
 * The third post-state question (PRD 30 §4.1), shown only when this meeting
 * actually changes something: what we know, what was heard, one tap.
 */
function ChangesWhatWeKnow({ meetingId }: { meetingId: string }): ReactElement | null {
  const k = useKnowledge({ meeting: meetingId });
  const { tap } = useKnowledgeActions();
  const { show } = useNotices();
  const changes = (k.data?.proposed ?? []).filter((e) => e.supersedes);
  const proposed = (k.data?.proposed ?? []).filter((e) => !e.supersedes);
  if (!k.data || (changes.length === 0 && proposed.length === 0)) return null;
  const act = (id: string, t: 'keep' | 'keep-both' | 'decline', said: string) =>
    tap.mutate(
      { id, tap: t },
      {
        onSuccess: () => show({ tone: 'success', text: said }),
        onError: (err) => show({ tone: 'failure', text: err.message }),
      },
    );
  return (
    <Card
      id="produced-knowledge"
      title="Does this change what we know?"
      aside={
        <Link to="/knowledge" className="t-secondary">
          {proposed.length > 0
            ? `${proposed.length} more ${proposed.length === 1 ? 'statement' : 'statements'} proposed from here →`
            : 'Knowledge →'}
        </Link>
      }
    >
      {changes.length === 0 ? (
        <p className="t-secondary">Nothing here contradicts what we know.</p>
      ) : (
        <div className="stack" style={{ gap: 12 }}>
          {changes.map((e) => (
            <div key={e.id} className="third">
              <div className="line old">
                <span className="mono">we know</span>
                <span>{e.supersedes?.statement}</span>
                <span />
              </div>
              <div className="line">
                <span className="mono">heard</span>
                <span>{e.statement}</span>
                <Pill>{e.grade.toLowerCase()}</Pill>
              </div>
              <div className="acts">
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => act(e.id, 'keep', 'Replaced.')}
                  disabled={tap.isPending}
                >
                  Replace
                </Button>
                <Button
                  size="sm"
                  onClick={() => act(e.id, 'keep-both', 'Kept both.')}
                  disabled={tap.isPending}
                >
                  Keep both
                </Button>
                <Button
                  variant="quiet"
                  size="sm"
                  onClick={() => act(e.id, 'decline', 'No change.')}
                  disabled={tap.isPending}
                >
                  No change
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

/** Note states, all four: writing up · written · too short · failed. */
/**
 * What the notetaker is doing, and the controls for it — on the meeting's own
 * page, in every state (PRD 43).
 *
 * Vladimir, 2026-09-20: *"how can I write notes during the meeting if I can't
 * open meeting page… just add there proper state for before, during recording,
 * during processing, after. For each state add controls for the bot."*
 *
 * Until then a meeting had no page until its recording had been ingested, so
 * the states below did not exist to show: the page was only ever read
 * afterwards. Now the row is created when the bot is sent, and this is the
 * band across the top of it.
 */
/** What is still on its way, named for a person rather than for the database. */
function coming(p: MeetingDetail['processing']): string[] {
  return [
    ...(p.note ? ['the note'] : []),
    ...(p.ledger ? ['what was promised and decided'] : []),
    ...(p.walkthrough ? ['the walkthrough'] : []),
  ];
}

const list = (parts: string[]): string =>
  parts.length < 2 ? (parts[0] ?? '') : `${parts.slice(0, -1).join(', ')} and ${parts.at(-1)}`;

/**
 * ✦ Rewrite — asks again for whatever of the write-up has not arrived.
 *
 * ⚠ The messages it sits in told people to press it for three weeks before it
 * existed. Vladimir, 2026-09-23: *"i want a button that is mentioned in the
 * label, its nowhere to find now"*. The server re-queues exactly what is
 * missing and nothing that arrived, and restarts the clock the page judges
 * "too long" by — so the page goes back to "writing up…" at once.
 */
function Rewrite({ meeting }: { meeting: MeetingDetail }): ReactElement {
  const rewrite = useRewrite(meeting.id);
  const { show } = useNotices();
  // Asking again spends money and writes the record, so it is the editors'.
  if (!meeting.access.canEdit)
    return <span className="why">Somebody who can edit this meeting can ask again.</span>;
  const names = {
    note: 'the note',
    ledger: 'what was promised and decided',
    walkthrough: 'the walkthrough',
  };
  return (
    <Button
      size="sm"
      variant="primary"
      working={rewrite.isPending ? 'Asking…' : false}
      onClick={() =>
        rewrite.mutate(undefined, {
          onSuccess: ({ asked }) =>
            show({
              tone: 'success',
              text: `Asked again for ${list(asked.map((a) => names[a]))} — about a minute for an hour of talking.`,
            }),
          onError: (err) => show({ tone: 'failure', text: err.message }),
        })
      }
    >
      ✦ Rewrite
    </Button>
  );
}

/** Pressed Stop, and Recall has not yet said the bot is out of the call. */
const LEAVING = {
  tone: '',
  lead: 'Leaving…',
  then: 'Everything it heard is kept. The note follows in a minute.',
};

/**
 * Pressed Start now / Send the notetaker, and the bot is not in yet (2026-09-26).
 * The server books it in seconds, but the meeting only says JOINING once the
 * worker has, and until then the band went on saying "The notetaker will
 * come… press Start now" beside the same button — Vladimir, dictating it into
 * an Asana task from a test call: "when you call the bot before the meeting
 * started, after pressing the button nothing happens".
 */
const SENDING = {
  tone: 'band-amber',
  lead: 'The notetaker is on its way.',
  then: 'It joins in a few seconds. If the meeting has a waiting room, let it in.',
};
/** How long "on its way" stands before the button comes back, if nothing arrived. */
const SENDING_MS = 120_000;

/**
 * What the band says before anything has recorded (2026-09-22).
 *
 * ⚠ There used to be ONE sentence here — "Booked for this meeting. It joins as
 * the meeting starts" — shown whenever `captureHealth` was SCHEDULED. But
 * SCHEDULED is the state of **every** meeting born from the calendar, with a
 * notetaker or without one, so the page promised a bot for meetings that had
 * been turned off. Vladimir pressed *Don't record this one*, reloaded, and was
 * told the notetaker was still booked: *"still looks like the bot will join
 * now"*. The verdict is the dispatcher's own, and now it arrives with the
 * meeting.
 */
function ahead(event: MeetingDetail['event']): { tone: string; lead: string; then: string } {
  if (!event) return AHEAD_DEFAULT;
  if (event.booked) return AHEAD_DEFAULT;
  switch (event.plan) {
    case 'turned-off-for-this-meeting':
      return {
        tone: '',
        lead: 'No notetaker for this one.',
        then: 'You skipped it. The series is unchanged — press Record it after all to undo.',
      };
    case 'turned-off-for-this-series':
      return {
        tone: '',
        lead: 'No notetaker for this one.',
        then: 'This series is turned off. You can still send one just for today.',
      };
    case 'one-to-ones-off':
      return {
        tone: '',
        lead: 'No notetaker for this one.',
        then: 'One-to-ones are off in your settings. You can still send one just for today.',
      };
    case 'not-turned-on':
      return {
        tone: '',
        lead: 'No notetaker for this one.',
        then: 'You record only the meetings you turn on — press Record this one to turn it on.',
      };
    case 'no-meeting-url':
      return {
        tone: '',
        lead: 'Nothing to join.',
        then: 'The calendar event has no meeting link.',
      };
    case 'unsupported-platform':
      return {
        tone: '',
        lead: 'Nothing to join.',
        then: 'The link is not a Meet, Zoom or Teams one.',
      };
    case 'longer-than-a-meeting':
      return {
        tone: '',
        lead: 'Nothing to record.',
        then: 'It runs longer than 6 hours — a block of time, not a call.',
      };
    case 'nobody-else-invited':
      return {
        tone: '',
        lead: 'No notetaker for this one.',
        then: 'Nobody else is invited, so there is no conversation to record.',
      };
    case 'free-month-ended':
      return {
        tone: 'band-amber',
        lead: 'No notetaker for this one.',
        then: 'The free month is over for everybody here who could record it. Everything recorded stays readable — ask your company about an agreement.',
      };
    case 'WILL_RECORD':
      return {
        tone: 'band-amber',
        lead: 'The notetaker will come.',
        then: 'It is booked shortly before the meeting starts — or press Start now to bring it in early.',
      };
    default:
      return AHEAD_DEFAULT;
  }
}

const AHEAD_DEFAULT = {
  tone: 'band-amber',
  lead: 'Booked for this meeting.',
  then: 'It joins as the meeting starts — or press Start now to bring it in early.',
};

const SAYS: Partial<Record<CaptureHealth, { tone: string; lead: string; then: string }>> = {
  SCHEDULED: AHEAD_DEFAULT,
  JOINING: {
    tone: 'band-amber',
    lead: 'The notetaker is joining.',
    then: 'A few seconds. Anything you write here is kept.',
  },
  WAITING_ROOM: {
    tone: 'band-amber',
    lead: 'Waiting to be let in.',
    then: 'Somebody in the meeting has to admit it. It waits five minutes, then leaves.',
  },
  RECORDING: {
    tone: 'band-red',
    lead: 'Recording.',
    then: 'Write anything you want kept alongside the transcript — it is yours, and never rewritten.',
  },
  PAUSED: {
    tone: '',
    lead: 'Paused.',
    then: 'The notetaker is still in the call and is writing nothing down.',
  },
};

function LiveBar({ meeting }: { meeting: MeetingDetail }): ReactElement | null {
  const cap = meeting.capture;
  // ⚠ The MEETING's state, not the run's. Since PRD 44 a meeting exists from
  // the moment it is on a calendar, so it can be SCHEDULED with no recording
  // behind it at all — which is the commonest state of all.
  const health = meeting.captureHealth;
  const bot = useBotAction();
  const sending = useSendBot();
  const skipping = useRecordOverride();
  const { show } = useNotices();
  // ⚠ Pause and resume are written down before the server answers, so a
  // re-read shows them at once. **Stop is not**: the bot has been asked to
  // leave and the health only moves when Recall says it has, seconds later.
  // Without this the band says "Recording." after you pressed Stop, which
  // reads as a button that did nothing.
  const [leaving, setLeaving] = useState(false);
  useEffect(() => {
    if (!HAPPENING.includes(health)) setLeaving(false);
  }, [health]);
  // Sent, and not in yet: said at once, the button gone — until the meeting
  // moves on, or two minutes pass with nothing (then it may be pressed again).
  const [sent, setSent] = useState(false);
  useEffect(() => {
    if (health !== 'SCHEDULED') setSent(false);
  }, [health]);
  useEffect(() => {
    if (!sent) return;
    const timer = window.setTimeout(() => setSent(false), SENDING_MS);
    return () => window.clearTimeout(timer);
  }, [sent]);
  const says = leaving
    ? LEAVING
    : health === 'SCHEDULED'
      ? sent
        ? SENDING
        : ahead(meeting.event)
      : SAYS[health];
  /** It would record if nothing were done — so there is something to call off. */
  const willRecord =
    !!meeting.event && (meeting.event.booked || meeting.event.plan === 'WILL_RECORD');
  // Being written up is a state of the meeting too, and the one people are
  // most likely to mistake for a fault (Vladimir, 2026-09-21: "I don't
  // understand if it's just processing or something is already broke").
  const making = coming(meeting.processing);
  if (!says && making.length) {
    // ⚠ And the honest version of the same band once the wait is absurd. The
    // whole point of the state is to tell a wait from a fault, so it must be
    // able to say "fault" — a cheerful "writing up…" that never resolves is
    // the bug it was meant to close.
    const stuck = meeting.processing.stalled;
    return (
      <div
        className={`band${stuck ? ' band-amber' : ''}`}
        style={{ flexDirection: 'column', alignItems: 'flex-start', gap: 6 }}
      >
        <strong>{stuck ? 'This is taking too long.' : 'Writing up…'}</strong>
        <span className="t-secondary">
          {stuck ? (
            <>
              {list(making)} {making.length === 1 ? 'was' : 'were'} due a quarter of an hour ago and
              {making.length === 1 ? ' has' : ' have'} not arrived. The transcript is safe and
              nothing is lost — the writing up is what failed, and ✦ Rewrite asks for it again.
            </>
          ) : (
            <>
              {list(making)} {making.length === 1 ? 'is' : 'are'} being made from what was said.
              About a minute for an hour of talking; the page brings them in as they arrive.
            </>
          )}
        </span>
        {stuck ? <Rewrite meeting={meeting} /> : null}
      </div>
    );
  }
  if (!says && !meeting.canSendBot) return null;

  const act = (action: 'stop' | 'pause' | 'resume') => {
    if (!cap) return;
    if (action === 'stop') setLeaving(true);
    bot.mutate(
      { runId: cap.runId, action },
      {
        onSuccess: () =>
          show({
            tone: 'success',
            text:
              action === 'stop'
                ? 'Stopped — the notetaker has left. Everything it heard so far is kept.'
                : action === 'pause'
                  ? 'Paused — it stays in the call and records nothing until you resume.'
                  : 'Recording again.',
          }),
        onError: (err) => {
          setLeaving(false);
          show({ tone: 'failure', text: err.message });
        },
      },
    );
  };

  const controls = leaving ? null : (
    <div className="cluster" style={{ gap: 8 }}>
      {cap?.canPause && (
        <Button size="sm" onClick={() => act('pause')} working={bot.isPending ? 'Pausing…' : false}>
          Pause
        </Button>
      )}
      {cap?.canResume && (
        <Button
          size="sm"
          variant="primary"
          onClick={() => act('resume')}
          working={bot.isPending ? 'Resuming…' : false}
        >
          Resume
        </Button>
      )}
      {cap?.canStop && (
        <Button
          size="sm"
          variant="danger"
          onClick={() => act('stop')}
          working={bot.isPending ? 'Stopping…' : false}
        >
          Stop
        </Button>
      )}
      {/* ⚠ The other direction, and it was missing everywhere. A notetaker is
          booked for a meeting somebody would rather keep to themselves and the
          only way to stop it was a menu on a list row that hid itself the
          moment a bot existed (Vladimir, 2026-09-22: "I cant stop bot to join
          the meeting from the meeting list or from the meeting page, for
          example i dont want him to join some meetings"). Cancelling a booked
          bot is Recall's own DELETE; one already in the call is Stop's job. */}
      {health === 'SCHEDULED' && meeting.event && willRecord && (
        <Button
          size="sm"
          working={skipping.isPending ? 'Calling it off…' : false}
          onClick={() =>
            skipping.mutate(
              { eventId: meeting.event!.id, record: false },
              {
                onSuccess: () =>
                  show({
                    tone: 'success',
                    text: 'Called off — no notetaker for this one. Press Send the notetaker to change your mind.',
                  }),
                onError: (err) => show({ tone: 'failure', text: err.message }),
              },
            )
          }
        >
          Don’t record this one
        </Button>
      )}
      {/* Somebody who records only what they turn on turns this one on here
          (2026-10-08) — the same answer as "Record this one" on the list. */}
      {health === 'SCHEDULED' && meeting.event?.plan === 'not-turned-on' && (
        <Button
          size="sm"
          variant="primary"
          working={skipping.isPending ? 'Turning it on…' : false}
          onClick={() =>
            skipping.mutate(
              { eventId: meeting.event!.id, record: true },
              {
                onSuccess: () =>
                  show({ tone: 'success', text: 'On — the notetaker comes to this one.' }),
                onError: (err) => show({ tone: 'failure', text: err.message }),
              },
            )
          }
        >
          Record this one
        </Button>
      )}
      {/* The way back. Skipping one meeting is a press somebody makes quickly,
          and a press with no undo beside it is one people learn not to make. */}
      {health === 'SCHEDULED' && meeting.event && meeting.event.recordOverride === false && (
        <Button
          size="sm"
          working={skipping.isPending ? 'Putting it back…' : false}
          onClick={() =>
            skipping.mutate(
              { eventId: meeting.event!.id, record: null },
              {
                onSuccess: () =>
                  show({ tone: 'success', text: 'Back on — it follows the series again.' }),
                onError: (err) => show({ tone: 'failure', text: err.message }),
              },
            )
          }
        >
          Record it after all
        </Button>
      )}
      {meeting.canSendBot && meeting.event && !sent && (
        <Button
          size="sm"
          variant="primary"
          working={sending.isPending ? 'Sending…' : false}
          onClick={() =>
            sending.mutate(meeting.event!.id, {
              onSuccess: (result) => {
                if (result.status === 'sending') setSent(true);
                show({
                  tone: 'success',
                  text:
                    result.status === 'already-there'
                      ? 'The notetaker is already in that meeting.'
                      : 'On its way — it joins in a few seconds.',
                });
              },
              onError: (err) => show({ tone: 'failure', text: err.message }),
            })
          }
        >
          {/* "Start now" only means something when one is coming anyway. On a
              meeting nobody is sending a bot to, the button IS the sending. */}
          {health === 'SCHEDULED' && willRecord ? 'Start now' : 'Send the notetaker'}
        </Button>
      )}
    </div>
  );

  // Over, and it can be sent back: no band, just the way to do it.
  if (!says) return <div className="cluster">{controls}</div>;

  return (
    <div
      className={`band ${says.tone}`}
      style={{ flexDirection: 'column', alignItems: 'flex-start', gap: 6 }}
    >
      <div className="between" style={{ width: '100%', gap: 12, alignItems: 'center' }}>
        <strong>
          {health === 'RECORDING' && <span className="dot dot-live" style={{ marginRight: 8 }} />}
          {says.lead}
        </strong>
        {controls}
      </div>
      <span className="t-secondary">{says.then}</span>
    </div>
  );
}

function NoteState({ meeting }: { meeting: MeetingDetail }): ReactElement {
  if (meeting.captureHealth === 'FAILED')
    return (
      <div className="stack-lg">
        <div
          className="band band-red"
          style={{ flexDirection: 'column', alignItems: 'flex-start', gap: 4 }}
          role="alert"
        >
          <strong>
            {meeting.captureReason === 'REMOVED' && meeting.capture?.stoppedBy
              ? `${meeting.capture.stoppedBy} sent the notetaker away.`
              : whyNothing(meeting.captureReason).title}
          </strong>
          <span>
            {meeting.captureReason === 'NOBODY_CAME' ? (
              <>
                It joined
                {meeting.capture?.scheduledFor ? ` at ${clock(meeting.capture.scheduledFor)}` : ''},
                waited five minutes with nobody else in the call and left — it is in no list, since
                nobody missed it.
              </>
            ) : meeting.captureReason === 'REMOVED' ? (
              meeting.capture?.stoppedBy ? (
                // Said by name: "nobody let it in" is not the story when a
                // person sent it away (2026-10-14).
                <>
                  They pressed Stop in Ptitim
                  {meeting.capture.endedAt ? ` at ${clock(meeting.capture.endedAt)}` : ''}
                  {meeting.capture.joinedAt ? '' : ', while it was still waiting to be let in'}.
                </>
              ) : (
                <>Somebody removed it from the call before anything was recorded.</>
              )
            ) : meeting.captureReason === 'NO_RECORDING' ? (
              <>It was in the call, but no recording came of it.</>
            ) : (
              <>
                It knocked
                {meeting.capture?.scheduledFor ? ` at ${clock(meeting.capture.scheduledFor)}` : ''},
                waited five minutes in the lobby and left.
              </>
            )}{' '}
            There is no transcript and no note for this hour — and nothing can be recovered
            afterwards.
          </span>
        </div>
        <div className="stack" style={{ gap: 6 }}>
          <span className="t-heading">What still exists</span>
          <ul className="t-secondary" style={{ margin: 0, paddingLeft: 18 }}>
            <li>
              The calendar event and its attendees
              {meeting.seriesKey ? (
                <>
                  , and{' '}
                  <Link to={`/series/${encodeURIComponent(meeting.seriesKey)}`}>
                    the series it belongs to
                  </Link>
                </>
              ) : null}
              .
            </li>
            {meeting.seriesKey ? (
              <li>
                Commitments from previous instances of this series, still open —{' '}
                <Link to={`/open?series=${encodeURIComponent(meeting.seriesKey)}`}>in Open</Link>.
              </li>
            ) : null}
            <li>
              Anything you typed, kept even though no audio was heard
              {meeting.notes.trim() ? ' — under What you typed' : ''}.
            </li>
          </ul>
        </div>
      </div>
    );
  if (HAPPENING.includes(meeting.captureHealth))
    return (
      <Empty lead="Not written yet.">
        The note is composed when the meeting ends. Until then, what you type under Your notes is
        the record — and it is never rewritten.
      </Empty>
    );
  // ⚠ The server's answer, not a guess from the clock. This used to read
  // "written within the last quarter of an hour, so probably still running",
  // which says "writing up…" about a note that failed an hour ago and "too
  // short" about one that is honestly still being written.
  if (meeting.processing.note)
    return meeting.processing.stalled ? (
      <Empty
        tone="bad"
        lead="This should have been written by now."
        action={<Rewrite meeting={meeting} />}
      >
        A quarter of an hour and no note. The transcript below is complete and safe — it is the
        writing up that failed.
      </Empty>
    ) : (
      <Empty lead="Writing up…">
        The transcript is already readable while this runs. About forty seconds for an hour.
      </Empty>
    );
  return (
    <Empty lead="Too short to write up.">
      Under two minutes of speech. The transcript stands alone; no note is invented from nothing.
    </Empty>
  );
}

/** What the meeting settled. Its own section, on the Decisions tab (PRD 54). */
function Decisions({
  meeting,
  onJump,
}: {
  meeting: MeetingDetail;
  onJump: (segmentId: string | null) => void;
}): ReactElement {
  const superseding = meeting.decisions.filter((d) => d.supersedesId).length;
  return (
    <RecordSection
      id="record-decisions"
      kind="decision"
      title="What was settled"
      count={meeting.decisions.length}
      nothing="Nothing was decided in this meeting."
      aside={
        superseding ? (
          <span className="mono">
            {superseding === 1
              ? 'one supersedes an earlier decision'
              : `${superseding} supersede earlier decisions`}
          </span>
        ) : (
          <span className="mono">what stands until something changes it</span>
        )
      }
    >
      <Rows>
        {meeting.decisions.map((d) => (
          <DecisionRow
            key={d.id}
            d={d}
            startedAt={meeting.startedAt}
            onJump={onJump}
            canEdit={meeting.access.canEdit}
          />
        ))}
      </Rows>
    </RecordSection>
  );
}

function Produced({
  meeting,
  onJump,
  names,
}: {
  meeting: MeetingDetail;
  onJump: (segmentId: string | null) => void;
  names: Map<string, string>;
}): ReactElement {
  const [posting, setPosting] = useState(false);
  // ⚠ Anybody who may edit the note may add one — the same right as rewording
  // one that is there (Vladimir, 2026-09-23: "add something myself").
  const add = meeting.access.canEdit ? (
    <AddAction meetingId={meeting.id} attendees={meeting.attendeeEmails} names={names} />
  ) : null;
  // ⚠ Dropped is never listed (Vladimir, 2026-09-28: "after dropping action
  // shall not be closed but DROPPED and it shall not be shown at all") —
  // except the one dropped a moment ago on this page: in its place, crossed
  // out, with Undo, until the page is left. A slip of the menu must not cost
  // the action.
  const [droppedHere, setDroppedHere] = useState<Commitment[]>([]);
  const listed = new Set(meeting.commitments.map((c) => c.id));
  const kept = droppedHere.filter((c) => !listed.has(c.id));
  const dropped = (c: Commitment) =>
    setDroppedHere((was) => [...was.filter((x) => x.id !== c.id), c]);
  const stillOpen = [...meeting.commitments.filter((c) => c.status === 'OPEN'), ...kept].sort(
    (a, b) => Date.parse(a.createdAt) - Date.parse(b.createdAt),
  );
  const openCount = stillOpen.filter((c) => c.status === 'OPEN').length;
  const finished = meeting.commitments.filter((c) => c.status !== 'OPEN');
  const nothing =
    meeting.commitments.length +
      kept.length +
      meeting.decisions.length +
      meeting.closedHere.length ===
    0;
  const anythingToPost = meeting.commitments.length + meeting.decisions.length > 0;
  const inSlack = meeting.commitments.some((c) => c.postedToSlack);

  // ⚠ One card while the ledger is still being read, not four empty ones.
  // "Being read…" said four times is four times as alarming and no more
  // informative, and "no risks were raised" is a lie until it has looked.
  if (nothing) {
    return (
      <Card id="produced-actions" title="What this meeting produced" flush>
        {meeting.processing.ledger ? (
          meeting.processing.stalled ? (
            <Empty
              tone="bad"
              lead="This should have been read by now."
              action={
                <>
                  <Rewrite meeting={meeting} />
                  {add}
                </>
              }
            >
              A quarter of an hour since the meeting was written up and the transcript has not been
              read for commitments. Nothing is lost.
            </Empty>
          ) : (
            <Empty lead="Being read…" action={add}>
              What was promised and what was decided are read out of the transcript once, after the
              meeting. This is not “nothing was promised” yet — it is not finished looking.
            </Empty>
          )
        ) : meeting.captureHealth === 'FAILED' ? (
          <Empty tone="bad" lead="Nothing was recorded, so nothing was produced." action={add}>
            The ledger is untouched; earlier commitments from this series stand as they were.
          </Empty>
        ) : (
          <Empty lead="Nobody promised anything, and nothing was decided." action={add}>
            That is a record, not a failure — most conversations are like this. The note and the
            transcript are one tab away, and the ledger is untouched.
          </Empty>
        )}
      </Card>
    );
  }

  return (
    <>
      {/* ⚠ Each record its own section with its own kind — not one card with
          everything in it. Vladimir, 2026-09-22: "I want to split meeting
          notes, action items, risks, decisions. Not everything in one meeting
          note" (PRD 54). */}
      <RecordSection
        id="produced-actions"
        kind="action"
        title="What was promised"
        count={meeting.commitments.length}
        nothing="Nobody promised anything here."
        aside={
          <div className="cluster" style={{ gap: 10 }}>
            {openCount ? <span className="mono">{openCount} still open</span> : null}
            {add}
            {/* ⚠ Only where somebody may act. A button that answers 404 is
                worse than a missing one, and posting a meeting's obligations
                is an act (PRD 33). */}
            {anythingToPost && meeting.access.canEdit ? (
              <Button size="sm" onClick={() => setPosting(true)}>
                {inSlack ? 'Update in Slack' : 'Post to Slack'}
              </Button>
            ) : null}
          </div>
        }
      >
        {posting ? <SlackPost meetingId={meeting.id} onClose={() => setPosting(false)} /> : null}
        <Rows>
          {stillOpen.map((c) => (
            <CommitmentRow
              key={c.id}
              c={c}
              meeting={meeting}
              onJump={onJump}
              names={names}
              onDropped={dropped}
            />
          ))}
          {/* ⚠ Under their own line. A finished one still belongs here — this
              is where it was born — but listed straight after the open ones it
              reads as open, which is what happened to Vladimir on 2026-09-20
              after ticking two of them off. */}
          {finished.length > 0 ? (
            <div className="row dense" style={{ background: 'var(--sunk)', minHeight: 30 }}>
              <span className="eyebrow">
                {finished.length === 1
                  ? 'And one that is finished'
                  : `And ${finished.length} that are finished`}
              </span>
            </div>
          ) : null}
          {finished.map((c) => (
            <CommitmentRow key={c.id} c={c} meeting={meeting} onJump={onJump} names={names} />
          ))}
        </Rows>
      </RecordSection>

      {/* ⚠ Its own block, never a column beside what was promised. Vladimir,
          2026-09-22: "Let's move closed by what was said to the separate
          block, not the column." Right on both counts: these are not this
          meeting's commitments at all — they belong to earlier meetings and
          this conversation is what ended them — and on most meetings the
          column was empty, spending half the width to say nothing. */}
      {meeting.closedHere.length > 0 ? (
        <section id="produced-closed" className="card card-flush">
          <header className="row" style={{ minHeight: 56 }}>
            <h2 className="t-heading cluster" style={{ gap: 10 }}>
              Closed here — by what was said
              <span className="count-badge">{meeting.closedHere.length}</span>
            </h2>
            <span className="grow" />
            <span className="mono">promised earlier, settled in this one</span>
          </header>
          <Rows>
            {meeting.closedHere.map((c) => (
              <CommitmentRow key={c.id} c={c} meeting={meeting} onJump={onJump} names={names} />
            ))}
          </Rows>
        </section>
      ) : null}
    </>
  );
}

/** Where a row of "What it produced" goes: a tab, and a section on it. */
interface Landing {
  view: View;
  anchor: string;
}

/**
 * What it produced, counted — each row a way to the section that lists it.
 *
 * ⚠ **Every row goes somewhere.** They all scrolled to one id that only the
 * EMPTY card carried, so on any meeting that had produced something — the
 * only meetings where the card is worth pressing — nothing moved. Vladimir,
 * 2026-09-23: *"do nothing on click … must redirect to the record and needed
 * section, also shall have more rows with risks, actions, questions"*. Since
 * decisions, risks and questions moved to their own Decisions tab (PRD 54), a
 * row that lands there has to switch the tab first.
 */
function ProducedCounts({
  meeting,
  onGo,
  names,
}: {
  meeting: MeetingDetail;
  onGo: (to: Landing) => void;
  names: Map<string, string>;
}): ReactElement {
  const knowledge = useKnowledge({ meeting: meeting.id });
  const proposed = knowledge.data?.proposed.length ?? 0;
  const open = (rows: Array<{ status: string }>): number =>
    rows.filter((r) => r.status === 'OPEN').length;
  const superseding = meeting.decisions.filter((d) => d.supersedesId).length;
  const rows: Array<{ n: number; label: string; note?: string; to: Landing | null }> = [
    {
      n: meeting.commitments.length,
      label: meeting.commitments.length === 1 ? 'commitment' : 'commitments',
      note: open(meeting.commitments) ? `${open(meeting.commitments)} still open` : undefined,
      to: { view: 'summary', anchor: 'produced-actions' },
    },
    // Only when there are some: most meetings close nothing from earlier, and
    // a row saying so every time is a row nobody reads.
    ...(meeting.closedHere.length
      ? [
          {
            n: meeting.closedHere.length,
            label: `earlier ${meeting.closedHere.length === 1 ? 'commitment' : 'commitments'} closed here`,
            to: { view: 'summary' as const, anchor: 'produced-closed' },
          },
        ]
      : []),
    {
      n: meeting.decisions.length,
      label: meeting.decisions.length === 1 ? 'decision' : 'decisions',
      note: superseding ? `${superseding} superseding` : undefined,
      to: { view: 'record', anchor: 'record-decisions' },
    },
    {
      n: meeting.risks.length,
      label: meeting.risks.length === 1 ? 'risk' : 'risks',
      note: open(meeting.risks) ? `${open(meeting.risks)} still open` : undefined,
      to: { view: 'record', anchor: 'record-risks' },
    },
    {
      n: meeting.questions.length,
      label: meeting.questions.length === 1 ? 'question' : 'questions',
      note: open(meeting.questions) ? `${open(meeting.questions)} unanswered` : undefined,
      to: { view: 'record', anchor: 'record-questions' },
    },
    {
      n: proposed,
      label: `${proposed === 1 ? 'statement' : 'statements'} proposed for Knowledge`,
      // The card they are listed on is only there when there are some.
      to: proposed ? { view: 'summary', anchor: 'produced-knowledge' } : null,
    },
  ];
  return (
    <Card title="What it produced" flush>
      {rows.map(({ n, label, note, to }) => (
        <button
          key={label}
          type="button"
          className="count-row"
          disabled={!to}
          onClick={to ? () => onGo(to) : undefined}
        >
          <span className="mono" style={{ width: 20 }}>
            {n}
          </span>
          <span className="grow">{label}</span>
          {note ? <span className="mono t-secondary">{note}</span> : null}
          {to ? <Icon name="chevron" size={15} className="t-secondary" /> : null}
        </button>
      ))}
      {meeting.access.canEdit ? (
        <AddAction
          meetingId={meeting.id}
          attendees={meeting.attendeeEmails}
          names={names}
          as="row"
        />
      ) : null}
    </Card>
  );
}

const count = (n: number, noun: string): string => `${n} ${noun}${n === 1 ? '' : 's'}`;

function CommitmentRow({
  c,
  meeting,
  onJump,
  names,
  onDropped,
}: {
  c: Commitment;
  meeting: MeetingDetail;
  onJump: (s: string | null) => void;
  names: Map<string, string>;
  /** Dropped here: kept in its place until the page is left (2026-09-28). */
  onDropped?: (dropped: Commitment) => void;
}): ReactElement {
  const ledger = useLedgerActions();
  const { show } = useNotices();
  const me = useMe();
  const [assigning, setAssigning] = useState(false);
  const [rewording, setRewording] = useState(false);
  const [sending, setSending] = useState(false);
  const [words, setWords] = useState(c.text);
  const reword = useUpdateCommitment();
  const open = c.status === 'OPEN';
  const mine = c.direction === 'I_OWE';
  const saveWords = () => {
    const next = words.trim();
    if (next.length < 3 || next === c.text) return setRewording(false);
    reword.mutate(
      { id: c.id, body: { text: next } },
      {
        onSuccess: () => {
          setRewording(false);
          show({ tone: 'success', text: 'Reworded — and confirmed, since a person wrote it.' });
        },
        onError: (err) => show({ tone: 'failure', text: err.message }),
      },
    );
  };
  const end = (status: 'DONE' | 'DROPPED') =>
    ledger.close.mutate(
      { id: c.id, body: { status } },
      {
        onSuccess: (item) => {
          if (status === 'DROPPED')
            onDropped?.({ ...c, status: item.status, closedAt: item.closedAt });
          show({
            tone: 'success',
            text:
              status === 'DONE'
                ? `Marked done — “${short(c.text)}” has left ${mine ? 'your' : 'their'} side of the ledger.`
                : `Dropped — “${short(c.text)}” leaves every list when you leave this page.`,
            action: { label: 'Undo', onSelect: () => ledger.reopen.mutate(c.id) },
          });
        },
        onError: (err) => show({ tone: 'failure', text: err.message }),
      },
    );
  return (
    <div className={`row ${!open ? 'leaving' : ''}`} style={!open ? { opacity: 0.6 } : undefined}>
      <div className="row-main">
        {rewording ? (
          /* ⚠ In place of the words, full width — the way Open does it. It sat
             after the buttons as a third column, a narrow box beside the very
             sentence it was editing (Vladimir, 2026-09-24: "Rewording action
             looks bad"). Blur does not save; the keys are said on screen. */
          <form
            className="stack"
            style={{ gap: 6, width: '100%' }}
            onSubmit={(e) => {
              e.preventDefault();
              saveWords();
            }}
          >
            <TextArea
              aria-label="What was promised"
              autoFocus
              rows={3}
              value={words}
              onChange={(e) => setWords(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Escape') setRewording(false);
                if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') saveWords();
              }}
            />
            <span className="cluster" style={{ gap: 8 }}>
              <Button
                type="submit"
                size="sm"
                variant="primary"
                working={reword.isPending ? 'Saving…' : false}
              >
                Save
              </Button>
              <Button type="button" size="sm" variant="quiet" onClick={() => setRewording(false)}>
                Cancel
              </Button>
              <span className="mono">⌘↵ saves · Esc cancels</span>
            </span>
          </form>
        ) : (
          <Link
            to={`/c/${c.id}`}
            className="row-title"
            // Struck through when it is done: the pills say so too, but a person
            // reading a column does not read pills.
            style={{ color: 'inherit', textDecoration: open ? undefined : 'line-through' }}
          >
            {c.text}
          </Link>
        )}
        <span className="row-sub">
          {c.ownerEmail ? (
            <Who
              email={c.ownerEmail}
              name={c.ownerName ?? names.get(c.ownerEmail.toLowerCase()) ?? null}
              me={mine}
            />
          ) : (
            // ⚠ The name the model heard, when we could not match it to an
            // address — "who is this?" threw away the one clue there is, and
            // made a matching failure look like an attribution failure.
            <Who email="" name={c.ownerName} />
          )}
          {c.span.startSegmentId && meeting.access.canReadTranscript ? (
            <button
              type="button"
              className="btn btn-quiet btn-sm mono"
              onClick={() => onJump(c.span.startSegmentId)}
              title="Open the moment it was said"
            >
              {c.span.startMs != null ? clockAt(meeting.startedAt, c.span.startMs) : 'said at…'}
            </button>
          ) : null}
          <span className="mono">
            {c.closedAt ? `${ended(c.status)} ${since(c.closedAt)}` : `${since(c.createdAt)}`}
          </span>
          <Grade value={c.grade} />
          {c.handedOverFrom ? (
            <Pill>from {firstName(c.handedOverFrom.name, c.handedOverFrom.email)}</Pill>
          ) : null}
          {/* ⚠ A converted one says where it went, and goes there. Its link
              lived only in the row's menu, which a closed row does not have
              — so it could not be reached at all (Vladimir, 2026-09-23: "i
              also need to see the link to asana task if its closed because
              its converted to asana"). */}
          {c.status === 'EXPORTED' && c.asanaTaskUrl ? (
            <a
              href={c.asanaTaskUrl}
              target="_blank"
              rel="noreferrer"
              title="Open the task in Asana"
              style={{ textDecoration: 'none' }}
            >
              <Pill tone="green">in Asana ↗</Pill>
            </a>
          ) : !open ? (
            <Pill>{c.status.toLowerCase().replace('_', ' ')}</Pill>
          ) : null}
          {c.postedToSlack ? <Pill tone="green">in Slack</Pill> : null}
        </span>
      </div>
      {meeting.access.canEdit && !rewording ? (
        <div className="row-actions">
          {open ? <DueButton id={c.id} dueAt={c.dueAt} /> : null}
          {open && !c.ownerEmail ? (
            // ⚠ "Give an owner" is a lie when the meeting already said whose
            // it is. What is missing is the ADDRESS — the thing that lets it
            // be tagged in Slack, counted on their page and mailed to them —
            // so the button asks the question that is actually open
            // (Vladimir, 2026-09-21: "this commitment shows me give an owner
            // button but Alina already selected as correct owner").
            <Button size="sm" variant="primary" onClick={() => setAssigning(true)}>
              {c.ownerName ? `Who is ${firstName(c.ownerName, '')}?` : 'Give an owner'}
            </Button>
          ) : null}
          {open && c.ownerEmail ? (
            <Button
              size="sm"
              onClick={() => end('DONE')}
              working={ledger.close.isPending ? '…' : false}
            >
              {mine ? 'Mark done' : 'Got it'}
            </Button>
          ) : null}
          {open ? (
            <MenuButton
              size="sm"
              variant="quiet"
              label="···"
              items={[
                {
                  label: 'Reword it…',
                  onSelect: () => {
                    setWords(c.text);
                    setRewording(true);
                  },
                },
                { label: 'Change the owner…', onSelect: () => setAssigning(true) },
                {
                  label: c.asanaTaskUrl ? 'Open in Asana' : 'Send to Asana…',
                  onSelect: () =>
                    c.asanaTaskUrl ? window.open(c.asanaTaskUrl, '_blank') : setSending(true),
                },
                { label: 'Dropped — nobody will do it', onSelect: () => end('DROPPED') },
              ]}
            />
          ) : c.status !== 'SUPERSEDED' ? (
            <Button size="sm" variant="quiet" onClick={() => ledger.reopen.mutate(c.id)}>
              Undo
            </Button>
          ) : null}
        </div>
      ) : null}
      {sending ? <AsanaModal item={c} onClose={() => setSending(false)} /> : null}
      {assigning ? (
        <OwnerModal
          item={c}
          heard={c.ownerName}
          candidates={[
            ...(me.data ? [{ email: me.data.email, name: me.data.name, me: true }] : []),
            ...meeting.attendeeEmails
              .filter((a) => a.toLowerCase() !== me.data?.email.toLowerCase())
              .map((a) => ({ email: a, name: names.get(a.toLowerCase()) ?? null })),
          ]}
          onClose={() => setAssigning(false)}
        />
      ) : null}
    </div>
  );
}

const short = (text: string): string => (text.length > 48 ? `${text.slice(0, 46)}…` : text);

function DecisionRow({
  d,
  startedAt,
  onJump,
  canEdit,
}: {
  d: Decision;
  startedAt: string;
  onJump: (s: string | null) => void;
  canEdit: boolean;
}): ReactElement {
  const [editing, setEditing] = useState(false);
  if (editing) {
    return (
      <div className="row stacked record">
        <RecordEditor
          kind="decisions"
          id={d.id}
          text={d.text}
          detail={null}
          onClose={() => setEditing(false)}
        />
      </div>
    );
  }
  return (
    <div className="row">
      <div className="row-main">
        <span className="row-title">{d.text}</span>
        <span className="row-sub">
          <Pill tone={d.status === 'STANDS' ? 'green' : undefined}>
            {d.status === 'STANDS' ? 'stands' : d.status.toLowerCase()}
          </Pill>
          {d.supersedesId ? <span>supersedes an earlier decision</span> : null}
          {d.span.startSegmentId ? (
            <button
              type="button"
              className="btn btn-quiet btn-sm mono"
              onClick={() => onJump(d.span.startSegmentId)}
              title="Open the moment it was said"
            >
              {d.span.startMs != null ? clockAt(startedAt, d.span.startMs) : 'said at…'}
            </button>
          ) : null}
          <Grade value={d.grade} />
        </span>
      </div>
      {/* ⚠ Decisions get corrected and deleted like every other record: the
          extractor reads a settled thing out of a conversation and is
          sometimes wrong about both the words and whether it was settled at
          all (PRD 54). */}
      {canEdit ? (
        <div className="row-actions">
          <RecordControls
            kind="decisions"
            id={d.id}
            statuses={[]}
            onEdit={() => setEditing(true)}
          />
        </div>
      ) : null}
    </div>
  );
}

/** How the notetaker did — and the video, when it is yours to see. */
function HowItWent({ meeting }: { meeting: MeetingDetail }): ReactElement {
  const cap = meeting.capture;
  const heard = cap?.recordedSeconds != null ? duration(cap.recordedSeconds) : null;
  return (
    <Card title="How the notetaker did">
      <div className="stack">
        {cap ? (
          <dl className="stack" style={{ gap: 6 }}>
            <Fact
              k="Joined at"
              v={
                cap.joinedAt
                  ? clock(cap.joinedAt)
                  : cap.health === 'FAILED' &&
                      (meeting.captureReason ?? 'NOT_LET_IN') === 'NOT_LET_IN'
                    ? 'never admitted'
                    : '—'
              }
            />
            <Fact
              k="Heard"
              v={
                heard ? `${heard} of ${duration(between(meeting.startedAt, meeting.endedAt))}` : '—'
              }
            />
            <Fact k="Language" v={meeting.language} />
            <Fact
              k="Video"
              v={
                meeting.video.available
                  ? `kept until ${meeting.video.keptUntil ? dateLabel(meeting.video.keptUntil) : '—'}`
                  : meeting.access.canReadTranscript
                    ? 'not kept'
                    : 'for the people who were there'
              }
            />
          </dl>
        ) : (
          <p className="t-secondary">No notetaker was involved in this one.</p>
        )}
        {meeting.video.available ? (
          <p className="t-secondary">
            The recording plays on the <strong>Walkthrough</strong> tab, with the meeting&rsquo;s
            sections under it.
          </p>
        ) : null}
        <p className="t-secondary">
          {meeting.transcript.removedAt
            ? `The words were removed on ${dateLabel(meeting.transcript.removedAt)}, as this organisation asks; the note stays.`
            : meeting.transcript.keptUntil
              ? `The words are kept until ${dateLabel(meeting.transcript.keptUntil)}; the note stays after that.`
              : 'The words are kept for as long as the meeting is.'}{' '}
          Everyone in the call saw the notetaker join.
        </p>
      </div>
    </Card>
  );
}

function Fact({ k, v }: { k: string; v: string }): ReactElement {
  return (
    <div className="between">
      <dt className="t-secondary">{k}</dt>
      <dd className="mono" style={{ margin: 0, color: 'var(--ink)' }}>
        {v}
      </dd>
    </div>
  );
}

function FullNote({
  meeting,
  lines,
  onShowOriginal,
}: {
  meeting: MeetingDetail;
  lines: number | null;
  /** A translated note is edited in its original (PRD 57): the way there. */
  onShowOriginal: () => void;
}): ReactElement {
  const save = useSaveNote(meeting.id);
  // ⚠ A translation is read, never edited in place: what is on screen would
  // be saved over the words the note was written in. The note's own flag, not
  // the page's — one foreign action item translates the page, not the note.
  const translated = meeting.noteForeign && !meeting.translation?.original;
  // ⚠ Being written again (✦ on What you typed): anything typed into it now
  // would be written over by the new note, so it is read, not edited.
  const rewriting = meeting.processing.note && !!meeting.polishedNotes;
  const editable = meeting.access.canEdit && !translated && !rewriting;
  // Once the editor is up it stays up — clearing every word must not swap it
  // for "no note yet" under the caret.
  const [writing, setWriting] = useState(!!meeting.polishedNotes);
  useEffect(() => {
    if (meeting.polishedNotes) setWriting(true);
  }, [meeting.polishedNotes]);
  return (
    <Card
      title="Full note"
      aside={
        meeting.enhancedAt ? (
          <span className="mono">
            written {since(meeting.enhancedAt)}
            {lines ? ` · from ${lines} lines` : ''}
          </span>
        ) : undefined
      }
    >
      {editable && writing ? (
        // Vladimir, 2026-09-25: "a good markdown editor like in granola" —
        // edited where it is read, kept as it is typed.
        <EditableNote
          value={meeting.polishedNotes}
          onSave={(markdown) => save.mutateAsync(markdown)}
          label="The note"
          placeholder="Write the note — # for a heading, - for a list, [] for an action item"
        />
      ) : meeting.polishedNotes ? (
        <div className="stack">
          {rewriting ? (
            <p className="t-secondary" role="status">
              Being written again, with what you typed — this is the note as it was until the new
              one arrives.
            </p>
          ) : null}
          {meeting.access.canEdit && translated ? (
            <p className="t-secondary">
              This is a translation.{' '}
              <button type="button" className="linkish" onClick={onShowOriginal}>
                Show the original
              </button>{' '}
              to edit the note.
            </p>
          ) : null}
          <div className="note" style={{ maxWidth: '68ch' }}>
            <NoteText>{meeting.polishedNotes}</NoteText>
          </div>
        </div>
      ) : (
        <div className="stack">
          <NoteState meeting={meeting} />
          {editable && !HAPPENING.includes(meeting.captureHealth) && !meeting.processing.note ? (
            <div>
              <Button size="sm" onClick={() => setWriting(true)}>
                Write it yourself
              </Button>
            </div>
          ) : null}
        </div>
      )}
    </Card>
  );
}

/** Kept exactly as written. In nineteen meetings out of twenty this is empty — and that is fine. */
/**
 * The meeting retold in order — sections you open one at a time (PRD 47).
 *
 * Vladimir: *"00:00:01 discussed how was the weekend - expand - Artur said so,
 * Ian said so etc then next section so I can read very detailed meeting
 * retelling and decide myself what's important."*
 *
 * So every section is closed until asked for, and the closed row carries the
 * two things somebody scans by: when it happened and what it was about. The
 * judgement of what mattered is deliberately absent — that is the note's job,
 * and this exists for the times the note got it wrong.
 */
function Walkthrough({
  chapters,
  pending,
  stalled,
  onJump,
  placed,
  onPlay,
  rewrite,
}: {
  chapters: ReturnType<typeof useWalkthrough>;
  pending: boolean;
  stalled: boolean;
  onJump: (segmentId: string | null) => void;
  /** Each chapter's second in the recording, when there is one and it can be placed. */
  placed: Map<string, number>;
  /** Moves the recording above. Null when this meeting has none. */
  onPlay: ((seconds: number) => void) | null;
  /** ✦ Rewrite, for when it never came. */
  rewrite: ReactNode;
}): ReactElement {
  const [open, setOpen] = useState<Set<string>>(new Set());
  const toggle = (id: string) =>
    setOpen((was) => {
      const next = new Set(was);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  if (chapters.isPending) {
    return (
      <Card flush>
        <SkeletonRows rows={6} />
      </Card>
    );
  }
  if (chapters.isError) {
    return (
      <Card flush>
        <Broken what="The walkthrough" retry={() => void chapters.refetch()} />
      </Card>
    );
  }
  if (!chapters.data.length) {
    return (
      <Card flush>
        {pending && stalled ? (
          <Empty tone="bad" lead="This should have been written by now." action={rewrite}>
            A quarter of an hour and no walkthrough. The transcript is complete — it is the writing
            up that failed.
          </Empty>
        ) : pending ? (
          <Empty lead="Being written…">
            The walkthrough is composed once, after the meeting, from what was said. It arrives
            within a minute or so of the recording ending.
          </Empty>
        ) : (
          <Empty lead="Not written for this one.">
            A conversation too short to have sections does not get one.
          </Empty>
        )}
      </Card>
    );
  }

  return (
    <Card
      title="Walkthrough"
      flush
      aside={
        <div className="cluster" style={{ gap: 10 }}>
          <span className="mono">{count(chapters.data.length, 'section')}</span>
          <button
            type="button"
            className="btn btn-quiet btn-sm"
            onClick={() =>
              setOpen((was) => (was.size ? new Set() : new Set(chapters.data.map((c) => c.id))))
            }
          >
            {open.size ? 'Collapse all' : 'Expand all'}
          </button>
        </div>
      }
    >
      <div className="rows">
        {chapters.data.map((c) => {
          const shown = open.has(c.id);
          // The recording's clock when there is one, so a row and its button
          // under the player say the same second; the walkthrough's own clock,
          // which counts from the first word, when there is not.
          const second = placed.get(c.id);
          return (
            <div key={c.id} className="stack" style={{ gap: 0 }}>
              <button
                type="button"
                className="row"
                aria-expanded={shown}
                onClick={() => toggle(c.id)}
                style={{ width: '100%', textAlign: 'left', background: 'none', border: 0 }}
              >
                <span className="mono" style={{ width: 72 }}>
                  {second === undefined ? hms(c.at) : timecode(second)}
                </span>
                <span className="row-main">
                  <span className="row-title">{c.title}</span>
                </span>
                <Icon name="chevron" size={13} rotate={shown ? 270 : 90} />
              </button>
              {shown ? (
                <div
                  className="stack"
                  style={{ gap: 8, padding: '4px 16px 16px 88px', maxWidth: '68ch' }}
                >
                  <p className="t-body" style={{ whiteSpace: 'pre-wrap', margin: 0 }}>
                    {c.body}
                  </p>
                  <div className="cluster" style={{ gap: 8 }}>
                    {onPlay && second !== undefined ? (
                      <button
                        type="button"
                        className="btn btn-quiet btn-sm"
                        onClick={() => onPlay(second)}
                      >
                        Play from here
                      </button>
                    ) : null}
                    {c.startSegmentId ? (
                      <button
                        type="button"
                        className="btn btn-quiet btn-sm"
                        onClick={() => onJump(c.startSegmentId)}
                      >
                        Read the words
                      </button>
                    ) : null}
                  </div>
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    </Card>
  );
}

/** `00:14:02` — the same clock the model was shown. */
function hms(ms: number): string {
  const total = Math.max(0, Math.round(ms / 1000));
  return [Math.floor(total / 3600), Math.floor((total % 3600) / 60), total % 60]
    .map((n) => String(n).padStart(2, '0'))
    .join(':');
}

/**
 * What somebody typed while it happened — never rewritten by anything, and
 * since PRD 62 what the note is written FROM, beside what was said (Vladimir,
 * 2026-09-25: "check that it even affects the note" — until then it did not).
 * The same editor as the note: typed Markdown becomes structure, kept as it is
 * typed.
 */
function Typed({
  meeting,
  lines,
}: {
  meeting: MeetingDetail;
  /** Lines in the transcript, null while it loads: nothing said is nothing to write a note from. */
  lines: number | null;
}): ReactElement {
  const happening = HAPPENING.includes(meeting.captureHealth);
  const save = useSaveNote(meeting.id, 'notes');
  const again = useRewriteNote(meeting.id);
  const [asking, setAsking] = useState(false);
  const { show } = useNotices();
  if (!meeting.access.canEdit && !meeting.notes.trim()) {
    return (
      <Card flush>
        <Empty lead="Nothing was typed.">The note was written from the conversation.</Empty>
      </Card>
    );
  }
  const title = happening ? 'Your notes' : 'What you typed';
  // Typed after the note was written: it reaches the note only when the note
  // is written again, which replaces it — so it is asked for, never automatic.
  const rewritable =
    meeting.access.canEdit &&
    meeting.captureHealth === 'DONE' &&
    !!meeting.polishedNotes &&
    !meeting.processing.note &&
    meeting.notes.trim() !== '' &&
    lines !== 0;
  return (
    <Card title={title} aside={<span className="mono">kept exactly as written</span>}>
      <div className="stack">
        {meeting.access.canEdit ? (
          <EditableNote
            key={meeting.id}
            value={meeting.notes}
            onSave={(markdown) => save.mutateAsync(markdown)}
            label={title}
            placeholder={
              happening
                ? 'Write while it is happening — # for a heading, - for a list, [] for an action item'
                : 'Nothing typed during the call. Anything you add here is kept as yours.'
            }
          />
        ) : (
          <div className="note" style={{ maxWidth: '68ch' }}>
            <NoteText breaks>{meeting.notes}</NoteText>
          </div>
        )}
        {happening ? (
          <p className="t-secondary">
            When it ends, the note is written from these and from what was said — and these stay
            exactly as you typed them.
          </p>
        ) : meeting.processing.note && meeting.notes.trim() ? (
          <p className="t-secondary">
            The note is being written — from these and from what was said.
          </p>
        ) : rewritable ? (
          <p className="t-secondary">
            Typed something after the note was written?{' '}
            <button type="button" className="linkish" onClick={() => setAsking(true)}>
              ✦ Write the note again with these
            </button>
          </p>
        ) : null}
      </div>
      {asking ? (
        <Confirm
          title="Write the full note again?"
          noun="Write it again"
          working={again.isPending}
          onClose={() => setAsking(false)}
          onConfirm={() =>
            again.mutate(undefined, {
              onSuccess: () => {
                setAsking(false);
                show({
                  tone: 'success',
                  text: 'Writing the note again — it arrives on the Full note tab in about a minute.',
                });
              },
              onError: (e) => show({ tone: 'failure', text: e.message }),
            })
          }
        >
          It is written again from the transcript and from what you typed, and replaces the full
          note as it is now — changes made to it by hand included. Actions, decisions and write-ups
          are not touched.
        </Confirm>
      ) : null}
    </Card>
  );
}

/** One row of the transcript: a line said, a message typed, or people arriving or leaving. */
type TranscriptItem =
  | { at: number; said: TranscriptLine; typed?: never; came?: never }
  | { at: number; typed: ChatMessage; said?: never; came?: never }
  | { at: number; came: PresenceEvent[]; said?: never; typed?: never };

function Transcript({
  meeting,
  lines,
  chat,
  presence,
  jumpTo,
  names,
  videoZero,
  onWatch,
}: {
  meeting: MeetingDetail;
  lines: ReturnType<typeof useTranscript>;
  /** What was typed in the call — beside the words, never merged into them. */
  chat: ChatMessage[];
  /** Who came into the call and who left it — lines of their own, at their moment. */
  presence: PresenceEvent[];
  jumpTo: string | null;
  names: Map<string, string>;
  /** The wall-clock moment of the video's 00:00 — null when there is no video. */
  videoZero: number | null;
  /**
   * Opens the recording at this second, on the Walkthrough tab. Null when this
   * meeting has none, and the times are then plain text.
   */
  onWatch: ((seconds: number) => void) | null;
}): ReactElement {
  // What is being looked for in the words — in the address, like the tab.
  const [needle, setNeedle] = useUrlText('find');
  // ⚠ Once per line asked for. The lines change with every sentence while a
  // meeting is live (PRD 66), and scrolling back to the same line each time
  // would drag the page about under whoever is reading it.
  const jumped = useRef<string | null>(null);
  useEffect(() => {
    if (!jumpTo || jumped.current === jumpTo || !lines.data) return;
    const el = document.getElementById(`line-${jumpTo}`);
    if (!el) return;
    jumped.current = jumpTo;
    el.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }, [jumpTo, lines.data]);
  if (!meeting.access.canReadTranscript) {
    return (
      <Card flush>
        <Empty lead="The transcript is for the people who were there.">
          The note is yours to read; the record itself stays with them.
        </Empty>
      </Card>
    );
  }
  if (lines.isPending)
    return (
      <Card flush>
        <SkeletonRows rows={8} dense />
      </Card>
    );
  if (lines.isError)
    return (
      <Card flush>
        <Broken what="The transcript" retry={() => void lines.refetch()} />
      </Card>
    );
  if (meeting.transcript.removedAt && lines.data.length === 0) {
    return (
      <Card flush>
        <Empty lead={`The transcript was removed on ${dateLabel(meeting.transcript.removedAt)}.`}>
          This organisation keeps the words people said for a set time, then removes them. The note,
          its commitments and its decisions stay.
        </Empty>
      </Card>
    );
  }
  const happening = HAPPENING.includes(meeting.captureHealth);
  if (happening && lines.data.length === 0) {
    // Nothing said yet, or the notetaker is not in the call yet. Either way
    // an empty transcript during a meeting is a wait, not a state (PRD 43).
    return (
      <Card flush>
        <Empty lead="Listening.">
          Each line appears here as soon as it is said, the newest at the top. Recording is what
          keeps them; this panel is only the view.
        </Empty>
      </Card>
    );
  }
  const q = needle.trim().toLowerCase();
  // ⚠ Interleaved, not merged. A chat message sits at its own moment among the
  // words and is visibly a different thing: it was TYPED, and unlike the
  // transcript it is exact — a link or a spelling from the chat is what the
  // person actually wrote (PRD 50).
  const sorted: TranscriptItem[] = [
    ...lines.data.map((said) => ({
      at: said.spokeAt ? new Date(said.spokeAt).getTime() : 0,
      said,
    })),
    ...chat.map((typed) => ({ at: new Date(typed.sentAt).getTime(), typed })),
    // Who came and went (2026-09-25): "I did not see him on the meeting, I
    // wonder if he was even there" had no answer until these were kept.
    ...presence.map((came) => ({ at: new Date(came.at).getTime(), came: [came] })),
  ].sort((a, b) => a.at - b.at);
  // Arrivals with nothing said between them are one line: twenty people
  // joining at the top of the hour is one fact, not twenty rows.
  const stream: TranscriptItem[] = [];
  for (const x of sorted) {
    const last = stream.at(-1);
    if (x.came && last?.came && last.came[0].kind === x.came[0].kind) last.came.push(...x.came);
    else stream.push(x.came ? { at: x.at, came: [...x.came] } : x);
  }
  // A search is for words, so arrivals step aside while one is typed.
  const found = q
    ? stream.filter((x) => (x.said?.text ?? x.typed?.text ?? '').toLowerCase().includes(q))
    : stream;
  // ⚠ Newest first, always (PRD 66). Vladimir, 2026-10-08: "the new line
  // shall appear on top, not on the bottom", then "always show transcript
  // newest first, do not switch before-after call this is missleading".
  // Nothing scrolls to a new line: it arrives where the eye already is.
  const shown = [...found].reverse();
  return (
    <Card
      title="Transcript"
      flush
      aside={
        <span className="mono">
          {happening ? 'live · ' : ''}newest first · {count(lines.data.length, 'turn')}
          {chat.length ? ` · ${count(chat.length, 'message')} typed` : ''}
          {meeting.transcript.keptUntil
            ? ` · kept until ${dateLabel(meeting.transcript.keptUntil)}`
            : ''}
        </span>
      }
    >
      <div style={{ padding: '12px 20px' }}>
        <SearchField
          placeholder="Find a word in this transcript"
          value={needle}
          onChange={(e) => setNeedle(e.target.value)}
          kbd=""
        />
      </div>
      {shown.length === 0 ? (
        <Empty lead={q ? `Nothing in this transcript matches “${needle}”.` : 'Nothing was said.'} />
      ) : (
        <Rows>
          {shown.map((x) =>
            x.said ? (
              <Line
                key={x.said.id}
                l={x.said}
                meeting={meeting}
                lit={jumpTo === x.said.id}
                names={names}
                onWatch={onWatch}
                videoZero={videoZero}
              />
            ) : x.typed ? (
              <ChatLine
                key={x.typed.id}
                m={x.typed}
                at={onWatch && videoZero !== null ? (x.at - videoZero) / 1000 : null}
                onWatch={onWatch}
              />
            ) : (
              <PresenceLine
                key={x.came[0].id}
                people={x.came}
                // Somebody already in the room when the recording began arrives
                // at its first second, never before it.
                at={onWatch && videoZero !== null ? Math.max(0, (x.at - videoZero) / 1000) : null}
                onWatch={onWatch}
              />
            ),
          )}
        </Rows>
      )}
    </Card>
  );
}

/**
 * Where this meeting is filed — visible on the meeting itself, not buried in
 * the share panel. A folder that caught it by its own rule says so and cannot
 * be "unfiled" by accident: taking it out is recorded, so the rule does not
 * pull it back.
 */
/**
 * The channels this meeting is posted in (PRD 58), and "Post to a channel"
 * for whoever may share it — posting IS sharing, with everybody in the channel.
 */
function InChannels({ meeting }: { meeting: MeetingDetail }): ReactElement | null {
  const channels = useChannels();
  const actions = useChannelActions();
  const { show } = useNotices();
  const [posting, setPosting] = useState(false);
  const canPost = meeting.access.canManage;
  const held = meeting.channels;
  const rest = (channels.data?.mine ?? []).filter((c) => !held.some((h) => h.id === c.id));
  const fail = (err: Error) => show({ tone: 'failure', text: err.message });
  if (!canPost && held.length === 0) return null;
  return (
    <p className="cluster" style={{ gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
      <span className="t-secondary">In collections</span>
      {held.length === 0 ? <span className="t-secondary">— in none yet</span> : null}
      {held.map((c) => (
        <span key={c.id} className="chip">
          <Link to={`/ch/${c.id}`} style={{ color: 'inherit' }}>
            {c.name}
          </Link>
          {c.canRemove ? (
            <button
              type="button"
              className="btn btn-quiet btn-sm"
              aria-label={`Take it out of ${c.name}`}
              style={{ marginLeft: 4, padding: 0 }}
              onClick={() =>
                actions.unpost.mutate(
                  { id: c.id, meetingId: meeting.id },
                  {
                    onSuccess: () => show({ tone: 'success', text: `Out of ${c.name}.` }),
                    onError: fail,
                  },
                )
              }
            >
              <Icon name="close" size={12} />
            </button>
          ) : null}
        </span>
      ))}
      {canPost && rest.length > 0 ? (
        <Button size="sm" variant="quiet" onClick={() => setPosting(true)}>
          Add to collections…
        </Button>
      ) : null}
      {posting ? (
        <PostToChannels meetingId={meeting.id} channels={rest} onClose={() => setPosting(false)} />
      ) : null}
      {canPost && channels.isSuccess && channels.data.mine.length === 0 ? (
        <Link to="/channels?new=1" className="mono">
          + New collection
        </Link>
      ) : null}
    </p>
  );
}

/**
 * One transcript line — and, for editors, the pen: fix the words, fix the
 * speaker. The original stays on the row and every fix is a correction row;
 * the line's id never changes, so every span and citation still points here.
 */
/**
 * The message, with the URLs in it made into links where they were written.
 *
 * ⚠ In place, not repeated underneath. The link is part of the sentence
 * somebody typed — "the revised pricing is here <url>" — and listing it again
 * below says the same thing twice.
 *
 * Longest first, so a link that happens to be the prefix of another (a folder
 * and a file inside it, pasted in one message) does not swallow it.
 */
function withLinks(text: string, links: string[]): ReactNode[] {
  if (links.length === 0) return [text];
  const longestFirst = [...links].sort((a, b) => b.length - a.length);
  const out: ReactNode[] = [];
  let rest = text;
  let key = 0;
  while (rest.length > 0) {
    let best: { at: number; href: string } | null = null;
    for (const href of longestFirst) {
      const at = rest.indexOf(href);
      if (at !== -1 && (best === null || at < best.at)) best = { at, href };
    }
    if (best === null) {
      out.push(rest);
      break;
    }
    if (best.at > 0) out.push(rest.slice(0, best.at));
    out.push(
      // Opened by the reader, never by us — and without handing this page's
      // address to whatever host somebody pasted.
      <a key={(key += 1)} href={best.href} target="_blank" rel="noreferrer" className="chat-link">
        {best.href}
      </a>,
    );
    rest = rest.slice(best.at + best.href.length);
  }
  return out;
}

/**
 * One message somebody typed in the call (PRD 50).
 *
 * Deliberately not shaped like a transcript line: it is tagged, its text is
 * shown as written, and its links are links. A chat message is the only part
 * of a meeting that is exact — everything around it is a machine's reading of
 * speech — and blending the two would throw that away.
 *
 * ⚠ We show what somebody pasted in a private meeting; we neither fetch it
 * ourselves nor hand it this page's address on the way out.
 */
function ChatLine({
  m,
  at,
  onWatch,
}: {
  m: ChatMessage;
  /** Where in the recording it was sent, when there is one to open. */
  at: number | null;
  onWatch: ((seconds: number) => void) | null;
}): ReactElement {
  return (
    <div className="row dense chat-line">
      {at === null || !onWatch ? (
        <span className="mono" style={{ width: 52 }}>
          {clock(m.sentAt)}
        </span>
      ) : (
        <button
          type="button"
          className="mono line-at"
          title={`Watch from ${timecode(at)}, on the Walkthrough tab`}
          onClick={() => onWatch(at)}
        >
          {timecode(at)}
        </button>
      )}
      <span style={{ width: 160, flex: 'none' }}>
        {m.byNotetaker ? (
          // The notetaker's own — its notice, a pause, an answer to "Ptitim, …"
          // (2026-09-26): said in the call's chat, so kept in the transcript.
          <span className="t-ui">Ptitim</span>
        ) : (
          <Who email={m.authorEmail ?? ''} name={m.authorName} />
        )}
      </span>
      <span className="t-body grow chat-text">
        <span className={`kind-tag ${m.byNotetaker ? 'kind-notetaker' : 'kind-chat'}`}>
          {m.byNotetaker ? 'notetaker' : 'chat'}
        </span>{' '}
        {withLinks(m.text, m.links)}
      </span>
    </div>
  );
}

/**
 * People arriving in the call or leaving it (2026-09-25).
 *
 * A quiet line of its own between the words, because who was in the room is
 * part of the record — Vladimir, after the AI Talks: *"I did not see him on the
 * meeting, I wonder if he was even there"*. Arrivals with nothing said between
 * them come as one line: the first names shown, the rest a click away.
 */
function PresenceLine({
  people,
  at,
  onWatch,
}: {
  people: PresenceEvent[];
  /** Where in the recording it happened, when there is one to open. */
  at: number | null;
  onWatch: ((seconds: number) => void) | null;
}): ReactElement {
  const [open, setOpen] = useState(false);
  const joined = people[0].kind === 'joined';
  const names = people.map((p) => p.name ?? 'Someone');
  const visible = open || names.length <= 5 ? names : names.slice(0, 4);
  const hidden = names.length - visible.length;
  return (
    <div className="row dense presence-line">
      {at === null || !onWatch ? (
        <span className="mono" style={{ width: 52 }}>
          {clock(people[0].at)}
        </span>
      ) : (
        <button
          type="button"
          className="mono line-at"
          title={`Watch from ${timecode(at)}, on the Walkthrough tab`}
          onClick={() => onWatch(at)}
        >
          {timecode(at)}
        </button>
      )}
      <span className="presence-text grow">
        <span className="presence-mark" aria-hidden="true">
          {joined ? '→' : '←'}
        </span>
        {hidden > 0 ? (
          <>
            {visible.join(', ')} and{' '}
            <button type="button" className="presence-more" onClick={() => setOpen(true)}>
              {hidden} more
            </button>
          </>
        ) : (
          listed(visible)
        )}{' '}
        {joined ? 'joined' : 'left'}
      </span>
    </div>
  );
}

/** "Ann", "Ann and Bo", "Ann, Bo and Cy". */
function listed(names: string[]): string {
  if (names.length <= 1) return names[0] ?? '';
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

function Line({
  l,
  meeting,
  lit,
  names,
  onWatch,
  videoZero,
}: {
  l: TranscriptLine;
  meeting: MeetingDetail;
  lit: boolean;
  names: Map<string, string>;
  /** Opens the recording at this line, on the Walkthrough tab. Null when there is none. */
  onWatch: ((seconds: number) => void) | null;
  videoZero: number | null;
}): ReactElement {
  const [editingWords, setEditingWords] = useState(false);
  const [text, setText] = useState(l.text);
  const correct = useCorrectLine(meeting.id);
  const { show } = useNotices();

  const reassign = (speakerName: string, speakerEmail: string | null) => {
    if (speakerName === l.speakerName) return;
    correct.mutate(
      { segmentId: l.id, body: { speakerName, ...(speakerEmail ? { speakerEmail } : {}) } },
      {
        onSuccess: () =>
          show({ tone: 'success', text: `Now ${speakerName} — the original is kept.` }),
        onError: (err) => show({ tone: 'failure', text: err.message }),
      },
    );
  };
  const saveWords = () => {
    if (!text.trim() || text.trim() === l.text) return setEditingWords(false);
    correct.mutate(
      { segmentId: l.id, body: { text: text.trim() } },
      {
        onSuccess: () => {
          setEditingWords(false);
          show({ tone: 'success', text: 'Fixed — the original is kept on the record.' });
        },
        onError: (err) => show({ tone: 'failure', text: err.message }),
      },
    );
  };

  // The meeting's people, by name, to reassign a line in one click.
  const people = meeting.attendeeEmails.map((email) => ({
    email,
    name: names.get(email.toLowerCase()) ?? guessName(email),
  }));
  const editItems = [
    ...people.map((person) => ({
      label: `Said by ${person.name}`,
      sub: person.email,
      current: l.speakerName === person.name,
      onSelect: () => reassign(person.name, person.email),
    })),
    { label: 'Fix the words…', onSelect: () => setEditingWords(true) },
  ];

  if (editingWords) {
    return (
      <form
        id={`line-${l.id}`}
        className="row dense"
        style={{ alignItems: 'flex-start', gap: 8 }}
        onSubmit={(e) => {
          e.preventDefault();
          saveWords();
        }}
      >
        <span className="mono" style={{ width: 52 }}>
          {l.spokeAt ? clock(l.spokeAt) : ''}
        </span>
        <span style={{ width: 160, flex: 'none' }}>
          <Who email={l.speakerEmail ?? ''} name={l.speakerName} />
        </span>
        <input
          className="field grow"
          aria-label="The words"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => e.key === 'Escape' && (setText(l.text), setEditingWords(false))}
          autoFocus
        />
        <Button type="submit" size="sm" variant="primary" working={correct.isPending ? '…' : false}>
          Save
        </Button>
        <Button size="sm" variant="quiet" onClick={() => (setText(l.text), setEditingWords(false))}>
          Cancel
        </Button>
      </form>
    );
  }
  const at = onWatch ? secondsOfLine(l, videoZero) : null;
  return (
    <div
      id={`line-${l.id}`}
      className="row dense"
      style={lit ? { background: 'var(--green-tint)' } : undefined}
    >
      {at === null ? (
        <span className="mono" style={{ width: 52 }}>
          {l.spokeAt ? clock(l.spokeAt) : ''}
        </span>
      ) : (
        <button
          type="button"
          className="mono line-at"
          title={`Watch from ${timecode(at)}, on the Walkthrough tab`}
          onClick={() => onWatch?.(at)}
        >
          {timecode(at)}
        </button>
      )}
      <span style={{ width: 160, flex: 'none' }}>
        <Who email={l.speakerEmail ?? ''} name={l.speakerName} />
      </span>
      <span className="t-body grow">
        {l.text}
        {l.corrected ? (
          <span className="mono t-secondary" style={{ marginLeft: 8 }}>
            edited
          </span>
        ) : null}
      </span>
      {meeting.access.canEdit ? (
        <span className="line-edit">
          <MenuButton label="Edit" size="sm" variant="quiet" items={editItems} />
        </span>
      ) : null}
    </div>
  );
}

/**
 * Share — one control, three scales: a person, a space, and (as a fact) the
 * folders it is filed in — and every row says why. Two things the panel
 * refuses to hide: access you cannot remove (attendance, greyed with the
 * reason) and guests (anyone outside the organisation, in words, every time).
 */
function ShareModal({
  meeting,
  names,
  onClose,
}: {
  meeting: MeetingDetail;
  names: Map<string, string>;
  onClose: () => void;
}): ReactElement {
  const sharing = useSharing(meeting.id, true);
  const channels = useChannels();
  const everyChannel = [...(channels.data?.mine ?? []), ...(channels.data?.others ?? [])];
  const org = useOrg();
  const me = useMe();
  const actions = useShareActions(meeting.id);
  const { show } = useNotices();
  const [text, setText] = useState('');
  const home = me.data ? me.data.email.slice(me.data.email.indexOf('@') + 1).toLowerCase() : '';
  const orgName = org.data?.name ?? 'your organisation';
  const link = `${window.location.origin}/m/${meeting.id}`;
  const access = sharing.data?.linkAccess ?? meeting.linkAccess;
  const fail = (err: Error) => show({ tone: 'failure', text: err.message });

  const q = text.trim().toLowerCase();
  // ⚠ Channels, not spaces (PRD 58): posting to one shares it with everybody
  // in it — so only the ones this person is in are offered.
  const spaceHits = q
    ? (channels.data?.mine ?? []).filter(
        (c) => c.name.toLowerCase().includes(q) && !sharing.data?.spaces.some((x) => x.id === c.id),
      )
    : [];
  // ⚠ **No folders here.** Filing is not sharing — a folder grants nothing —
  // and it has its own control on the page, "In folders", where the meeting
  // can already be put into one and taken out again. Offering it here as well
  // made the same act live in two places and put a menu in front of somebody
  // who opened this to give a person access (Vladimir, 2026-09-21: "remove
  // option to share to the folder from the share modal").
  const emailHit = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(q) ? q : null;
  const addSpace = (id: string, name: string) =>
    actions.addToSpace.mutate(id, {
      onSuccess: () => {
        setText('');
        show({ tone: 'success', text: `Added to ${name} — everybody in it can open it now.` });
      },
      onError: fail,
    });
  const addPerson = (email: string) =>
    actions.grant.mutate(
      { email, role: 'VIEWER' },
      {
        onSuccess: () => {
          setText('');
          show({ tone: 'success', text: `${email} can read it now.` });
        },
        onError: fail,
      },
    );
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (spaceHits[0]) addSpace(spaceHits[0].id, spaceHits[0].name);
    else if (emailHit) addPerson(emailHit);
  };
  const setLink = (linkAccess: LinkAccess) =>
    actions.link.mutate(linkAccess, {
      onSuccess: () =>
        show({
          tone: 'success',
          text:
            linkAccess === 'INVITED'
              ? 'The link opens for nobody but the people above.'
              : linkAccess === 'DOMAIN'
                ? `Anyone at ${orgName} with the link can open it.`
                : 'Anyone with the link can open it.',
        }),
      onError: fail,
    });

  return (
    <Modal
      title={`Share “${meeting.title} — ${dayMonth(meeting.startedAt)}”`}
      onClose={onClose}
      foot={
        <div className="between" style={{ width: '100%', gap: 16 }}>
          <span className="t-secondary" style={{ maxWidth: 440 }}>
            Anyone without access who opens the link is told the meeting is not theirs and who was
            in it to ask — never a word of the content, and never a blank page.
          </span>
          <Button
            variant="primary"
            onClick={() =>
              void copyText(link).then(() => show({ tone: 'success', text: 'Link copied.' }))
            }
          >
            Copy link
          </Button>
        </div>
      }
    >
      <form onSubmit={submit} className="stack" style={{ gap: 6, position: 'relative' }}>
        <Field
          placeholder="Add a person by address, or a collection…"
          value={text}
          onChange={(e) => setText(e.target.value)}
          autoFocus
        />
        {/* ⚠ Only once something is typed. It used to open on an empty box, so
            the modal greeted everybody with a list they had not asked for and
            had to dismiss before doing the thing they came to do. */}
        {q && (spaceHits.length || emailHit) ? (
          <div className="float menu" style={{ position: 'static' }}>
            {spaceHits.map((sp) => (
              <button
                key={sp.id}
                type="button"
                className="menu-item"
                onClick={() => addSpace(sp.id, sp.name)}
              >
                {sp.name}
                <span className="why">
                  collection · {sp.memberCount} {sp.memberCount === 1 ? 'person' : 'people'} can
                  open it
                </span>
              </button>
            ))}
            {emailHit ? (
              <button type="button" className="menu-item" onClick={() => addPerson(emailHit)}>
                Add {emailHit}
                <span className="why">can read · no account needed yet</span>
              </button>
            ) : null}
          </div>
        ) : null}
      </form>
      {sharing.isPending ? (
        <SkeletonRows rows={2} dense />
      ) : sharing.isError ? (
        <p className="t-secondary red">{sharing.error.message}</p>
      ) : (
        <Rows>
          {sharing.data.people.map((p) => {
            const attended = meeting.attendeeEmails.some(
              (a) => a.toLowerCase() === p.email.toLowerCase(),
            );
            const guest = home && p.email.slice(p.email.indexOf('@') + 1).toLowerCase() !== home;
            return (
              <div key={p.email} className="row dense" style={{ paddingLeft: 0, paddingRight: 0 }}>
                <Who
                  email={p.email}
                  name={p.name ?? names.get(p.email.toLowerCase()) ?? guessName(p.email)}
                />
                <span className={`mono grow ${guest ? 'amber' : ''}`}>
                  {guest
                    ? `guest · outside ${orgName}`
                    : attended
                      ? 'was in the meeting'
                      : `added ${dayMonth(p.createdAt)}`}
                </span>
                {attended ? (
                  <Button
                    size="sm"
                    disabled
                    title="Was in the meeting — attendance cannot be un-invited"
                  >
                    {p.role === 'COLLABORATOR' ? 'Can edit' : 'Can read'}
                  </Button>
                ) : (
                  <Picker
                    aria-label={`Access of ${p.email}`}
                    value={p.role}
                    onChange={(role) =>
                      role === 'REMOVE'
                        ? actions.revoke.mutate(p.email, { onError: fail })
                        : actions.grant.mutate({ email: p.email, role }, { onError: fail })
                    }
                    size="sm"
                    width={130}
                    align="right"
                    options={[
                      { value: 'COLLABORATOR' as const, label: 'Can edit' },
                      { value: 'VIEWER' as const, label: 'Can read' },
                      { value: 'REMOVE' as const, label: 'Remove', danger: true },
                    ]}
                  />
                )}
              </div>
            );
          })}
          {/* The channels it is posted in (PRD 58). Its own home is not a
              place anybody chose, and is not listed. */}
          {sharing.data.spaces
            .filter((sp) => sp.id !== meeting.spaceId)
            .map((sp) => {
              const known = everyChannel.find((c) => c.id === sp.id);
              return (
                <div key={sp.id} className="row dense" style={{ paddingLeft: 0, paddingRight: 0 }}>
                  <Room name={sp.name} />
                  <span className="mono grow">
                    {known
                      ? `${known.memberCount} ${known.memberCount === 1 ? 'person' : 'people'} · `
                      : ''}
                    a collection it is in
                  </span>
                  <Picker
                    aria-label={`Access of ${sp.name}`}
                    value="OPEN"
                    onChange={(choice) =>
                      choice === 'REMOVE' &&
                      actions.removeFromSpace.mutate(sp.id, { onError: fail })
                    }
                    size="sm"
                    width={130}
                    align="right"
                    options={[
                      { value: 'OPEN' as const, label: 'Can open' },
                      { value: 'REMOVE' as const, label: 'Remove', danger: true },
                    ]}
                  />
                </div>
              );
            })}
        </Rows>
      )}
      <div
        className="stack"
        style={{ gap: 10, borderTop: '1px solid var(--line)', paddingTop: 12 }}
      >
        <div className="cluster" style={{ gap: 12, flexWrap: 'nowrap', alignItems: 'flex-start' }}>
          <Switch
            label=""
            checked={access !== 'INVITED'}
            onChange={(e) => setLink(e.target.checked ? 'DOMAIN' : 'INVITED')}
          />
          <span className="stack" style={{ gap: 2 }}>
            <span className="t-ui">Anyone at {orgName} with the link</span>
            <span className="t-secondary">
              everybody in your organisation could open it — nobody outside
            </span>
          </span>
        </div>
        <div className="cluster" style={{ gap: 12, flexWrap: 'nowrap', alignItems: 'flex-start' }}>
          <Switch
            label=""
            checked={access === 'PUBLIC'}
            onChange={(e) =>
              setLink(e.target.checked ? 'PUBLIC' : access === 'INVITED' ? 'INVITED' : 'DOMAIN')
            }
          />
          <span className="stack" style={{ gap: 2 }}>
            <span className="t-ui">Anyone with the link</span>
            <span className="t-secondary">
              outside your organisation too — for a note meant to be public
            </span>
          </span>
        </div>
      </div>
    </Modal>
  );
}
